import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Box,
  CheckCircle2,
  ClipboardList,
  Eye,
  Filter,
  Link2,
  Loader2,
  MapPin,
  PackageCheck,
  RefreshCw,
  Search,
  ShieldAlert,
  ShoppingCart,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';
import { getFileUrl } from '../services/api';
import {
  approveAdminSupplyHubProduct,
  approveAdminSupplyHubVendor,
  archiveSupplyHubProductMapping,
  cancelSupplyHubOrder,
  createAdminSupplyHubProduct,
  createAdminSupplyHubVendor,
  createSupplyHubProductMapping,
  createSupplyHubOrder,
  dismissSupplyHubRecommendation,
  generateCurrentSupplyHubRecommendations,
  getAdminSupplyHubProducts,
  getAdminSupplyHubVendors,
  getSupplyHubOrderById,
  getSupplyHubOrders,
  getSupplyHubProductMappings,
  getSupplyHubProductById,
  getSupplyHubProducts,
  getSupplyHubRecommendations,
  getSupplyHubVendorById,
  getSupplyHubVendors,
  rejectAdminSupplyHubProduct,
  rejectAdminSupplyHubVendor,
  receiveSupplyHubOrder,
  receiveSupplyHubOrderItem,
  sponsorAdminSupplyHubProduct,
  suspendAdminSupplyHubVendor,
  unsponsorAdminSupplyHubProduct,
  updateAdminSupplyHubProduct,
  updateAdminSupplyHubVendor,
} from '../services/supplyHubApi';
import {
  canBrowseSupplyHubMarketplace,
  canCreateSupplyHubOrders,
  canManageSupplyHubOrders,
  canManageSupplyHubRecommendations,
  canViewSupplyHubOrders,
  canViewSupplyHubRecommendations,
  normalizeRole,
  ROLE_KEYS,
} from '../utils/rolePermissions';

const DEFAULT_PRODUCTS_LIMIT = 24;
const DEFAULT_VENDORS_LIMIT = 50;
const DEFAULT_RECOMMENDATIONS_LIMIT = 50;
const DEFAULT_MAPPINGS_LIMIT = 50;
const DEFAULT_ORDERS_LIMIT = 50;
const ORDER_NOTES_MAX_LENGTH = 1000;
const RECEIVE_NOTES_MAX_LENGTH = 500;

const SUPPLY_HUB_TABS = Object.freeze({
  MARKETPLACE: 'marketplace',
  RESTOCK: 'restock',
  MAPPINGS: 'mappings',
  ORDERS: 'orders',
  ADMIN: 'admin',
});

const EMPTY_PRODUCT_FILTERS = {
  search: '',
  category: '',
  brand: '',
  vendorId: '',
  availabilityStatus: '',
  sponsoredOnly: false,
};

const EMPTY_VENDOR_FILTERS = {
  search: '',
  serviceArea: '',
  sponsoredOnly: false,
};

const EMPTY_ADMIN_VENDOR_FILTERS = {
  search: '',
  verificationStatus: '',
  sponsoredOnly: false,
};

const EMPTY_ADMIN_PRODUCT_FILTERS = {
  search: '',
  approvalStatus: '',
  availabilityStatus: '',
  vendorId: '',
  category: '',
  brand: '',
  sponsoredOnly: false,
};

const EMPTY_VENDOR_FORM = {
  displayName: '',
  legalName: '',
  contactEmail: '',
  contactPhone: '',
  serviceAreas: '',
  websiteUrl: '',
  gstNumber: '',
  publicDescription: '',
  logoUrl: '',
  sponsoredStatus: 'none',
};

const EMPTY_PRODUCT_FORM = {
  vendorId: '',
  name: '',
  category: '',
  brand: '',
  unit: '',
  packSize: '',
  description: '',
  imageUrl: '',
  listPrice: '',
  offerPrice: '',
  currency: 'INR',
  availabilityStatus: 'available',
  sponsoredStatus: 'none',
  sponsoredRank: '',
  tags: '',
};

function getFriendlySupplyHubError(error) {
  const status = error?.response?.status;
  const backendError = error?.response?.data?.error;

  if (status === 401) {
    return 'Please log in again to view Supply Hub.';
  }
  if (status === 403) {
    return backendError || 'Your role cannot access this Supply Hub action.';
  }
  if (status === 404) {
    return backendError || 'This Supply Hub listing is not available.';
  }
  if (status >= 500) {
    return 'Supply Hub is temporarily unavailable. Please try again.';
  }
  return backendError || 'Supply Hub request failed. Please try again.';
}

function formatMoney(amount, currency = 'INR') {
  if (amount === null || amount === undefined || amount === '') return 'Price not listed';
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return 'Price not listed';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

function formatLabel(value, fallback = 'Not specified') {
  const normalized = String(value || '').replace(/[_-]+/g, ' ').trim();
  if (!normalized) return fallback;
  return normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function getPriorityClasses(priority) {
  const normalized = String(priority || '').toLowerCase();
  if (normalized === 'critical') return 'border-rose-200 bg-rose-50 text-rose-700';
  if (normalized === 'high') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (normalized === 'medium') return 'border-blue-200 bg-blue-50 text-blue-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function getStockItemLabel(stockItem) {
  return [stockItem?.name, stockItem?.typeVariant].filter(Boolean).join(' - ') || 'Inventory item';
}

function getProductLabel(product) {
  return product?.name || 'Suggested Supply Hub product';
}

function getImageUrl(path) {
  if (!path) return '';
  return getFileUrl(path);
}

function getAvailabilityClasses(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'available') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (normalized === 'out_of_stock') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (normalized === 'discontinued') return 'border-slate-200 bg-slate-100 text-slate-600';
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function getSponsoredClasses(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'featured') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (normalized === 'sponsored') return 'border-cyan-200 bg-cyan-50 text-cyan-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function getOrderStatusClasses(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'requested') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (normalized === 'confirmed') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (normalized === 'shipped') return 'border-cyan-200 bg-cyan-50 text-cyan-700';
  if (normalized === 'received') return 'border-slate-200 bg-slate-100 text-slate-700';
  if (normalized === 'cancelled') return 'border-rose-200 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function getApprovalStatusClasses(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'approved') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (normalized === 'pending') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (normalized === 'rejected') return 'border-rose-200 bg-rose-50 text-rose-700';
  if (normalized === 'suspended') return 'border-slate-300 bg-slate-100 text-slate-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function getPublicUnitPrice(product) {
  const rawPrice = product?.offerPrice ?? product?.listPrice;
  const numericPrice = Number(rawPrice);
  if (!Number.isFinite(numericPrice) || numericPrice < 0) return null;
  return numericPrice;
}

function getVendorIdFromProduct(product) {
  return product?.vendor?.id || product?.vendorId || null;
}

function getVendorNameFromProduct(product) {
  return product?.vendor?.displayName || product?.vendorDisplayName || 'Approved vendor';
}

function getOrderItemCount(order) {
  return Array.isArray(order?.items) ? order.items.length : 0;
}

function canCancelOrder(order) {
  return ['draft', 'requested', 'confirmed'].includes(String(order?.orderStatus || '').toLowerCase());
}

function canReceiveStockForOrder(order) {
  return ['requested', 'confirmed', 'shipped'].includes(String(order?.orderStatus || '').toLowerCase());
}

function getOrderedQuantity(item) {
  const quantity = Number(item?.quantity || 0);
  return Number.isFinite(quantity) ? quantity : 0;
}

function getReceivedQuantity(item) {
  const quantity = Number(item?.receivedQuantity || 0);
  return Number.isFinite(quantity) ? quantity : 0;
}

function getRemainingReceiveQuantity(item) {
  return Math.max(0, getOrderedQuantity(item) - getReceivedQuantity(item));
}

function getReceivableItems(order) {
  if (!canReceiveStockForOrder(order)) return [];
  return (order?.items || []).filter((item) => getRemainingReceiveQuantity(item) > 0);
}

function hasMappedReceivableItem(order) {
  return getReceivableItems(order).some((item) => Boolean(item.currentStockId));
}

function listToCsv(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  return String(value || '');
}

function csvToList(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function getOrderMoneyText(order) {
  if (order?.orderValuesHidden) {
    return order?.financeLock?.message || 'Unlock Finance Lock to view order values.';
  }
  return formatMoney(order?.totalAmount, order?.currency);
}

function ModalShell({ title, eyebrow, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.18)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            {eyebrow ? (
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600">{eyebrow}</p>
            ) : null}
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[calc(92vh-84px)] overflow-y-auto px-5 py-5">{children}</div>
      </div>
    </div>
  );
}

function SafeImage({ src, alt, className }) {
  const imageUrl = getImageUrl(src);

  if (!imageUrl) {
    return (
      <div className={`${className} flex items-center justify-center bg-slate-100 text-slate-400`}>
        <Box className="h-8 w-8" />
      </div>
    );
  }

  return <img src={imageUrl} alt={alt} className={`${className} object-cover`} />;
}

function ProductCard({ product, canCreateOrder, onOpenProduct, onOpenVendor, onRequestOrder }) {
  const vendor = product?.vendor || null;
  const hasOffer = product?.offerPrice !== null && product?.offerPrice !== undefined;

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex gap-4 p-4">
        <SafeImage
          src={product?.imageUrl}
          alt={product?.name || 'Supply Hub product'}
          className="h-24 w-24 shrink-0 rounded-[14px] border border-slate-100"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2">
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold ${getAvailabilityClasses(product?.availabilityStatus)}`}>
              {formatLabel(product?.availabilityStatus)}
            </span>
            {product?.sponsoredStatus && product.sponsoredStatus !== 'none' ? (
              <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold ${getSponsoredClasses(product.sponsoredStatus)}`}>
                {formatLabel(product.sponsoredStatus)}
              </span>
            ) : null}
          </div>

          <h3 className="mt-3 line-clamp-2 text-base font-semibold tracking-tight text-slate-900">
            {product?.name || 'Unnamed product'}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {[product?.brand, product?.category].filter(Boolean).join(' | ') || 'Approved marketplace product'}
          </p>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 px-4 pb-4">
        <p className="line-clamp-3 min-h-[60px] text-sm leading-5 text-slate-600">
          {product?.description || 'No public product description has been added yet.'}
        </p>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-[14px] border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Unit</p>
            <p className="mt-1 font-semibold text-slate-800">{product?.unit || '-'}</p>
          </div>
          <div className="rounded-[14px] border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Pack</p>
            <p className="mt-1 font-semibold text-slate-800">{product?.packSize || '-'}</p>
          </div>
        </div>

        <div className="rounded-[14px] border border-slate-200 bg-white px-3 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Marketplace Price</p>
          <div className="mt-1 flex flex-wrap items-baseline gap-2">
            <span className="text-base font-semibold text-slate-900">
              {formatMoney(hasOffer ? product.offerPrice : product?.listPrice, product?.currency)}
            </span>
            {hasOffer && product?.listPrice !== null && product?.listPrice !== undefined ? (
              <span className="text-xs font-medium text-slate-400 line-through">
                {formatMoney(product.listPrice, product?.currency)}
              </span>
            ) : null}
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={() => vendor?.id && onOpenVendor(vendor.id)}
            disabled={!vendor?.id}
            className="min-w-0 text-left text-sm font-semibold text-blue-700 transition hover:text-blue-800 disabled:cursor-default disabled:text-slate-500"
          >
            <span className="block truncate">{vendor?.displayName || 'Approved vendor'}</span>
          </button>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => onRequestOrder(product)}
              disabled={!canCreateOrder}
              title={canCreateOrder ? 'Request this product from Supply Hub.' : 'Your role cannot create Supply Hub order requests.'}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-500"
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              Request
            </button>
            <button
              type="button"
              onClick={() => onOpenProduct(product.id)}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-blue-700"
            >
              <Eye className="h-3.5 w-3.5" />
              Details
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function VendorCard({ vendor, onOpenVendor }) {
  return (
    <article className="rounded-[18px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex items-start gap-3">
        <SafeImage
          src={vendor?.logoUrl}
          alt={vendor?.displayName || 'Supply Hub vendor'}
          className="h-14 w-14 shrink-0 rounded-[14px] border border-slate-100"
        />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-slate-900">{vendor?.displayName || 'Approved vendor'}</h3>
          <div className="mt-1 flex flex-wrap gap-2">
            <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
              Approved
            </span>
            {vendor?.sponsoredStatus && vendor.sponsoredStatus !== 'none' ? (
              <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getSponsoredClasses(vendor.sponsoredStatus)}`}>
                {formatLabel(vendor.sponsoredStatus)}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <p className="mt-3 line-clamp-3 text-sm leading-5 text-slate-600">
        {vendor?.publicDescription || 'No public vendor description has been added yet.'}
      </p>

      <div className="mt-3 flex items-start gap-2 text-xs text-slate-500">
        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span className="line-clamp-2">
          {Array.isArray(vendor?.serviceAreas) && vendor.serviceAreas.length
            ? vendor.serviceAreas.join(', ')
            : 'Service areas not listed'}
        </span>
      </div>

      <button
        type="button"
        onClick={() => onOpenVendor(vendor.id)}
        className="mt-4 w-full rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-700"
      >
        View Vendor
      </button>
    </article>
  );
}

function SupplyHubTabButton({ active, icon: Icon, label, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition ${
        active
          ? 'border-blue-200 bg-blue-600 text-white shadow-[0_10px_24px_rgba(37,99,235,0.2)]'
          : 'border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:text-blue-700'
      }`}
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {label}
      {count !== undefined ? (
        <span className={`rounded-full px-2 py-0.5 text-[11px] ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
          {count}
        </span>
      ) : null}
    </button>
  );
}

function RecommendationCard({
  recommendation,
  canManage,
  canCreateOrder,
  actioningId,
  onOpenProduct,
  onDismiss,
  onCreateMapping,
  onRequestOrder,
}) {
  const product = recommendation?.supplyHubProduct || null;
  const vendor = product?.vendor || null;
  const stockItem = recommendation?.currentStock || {};
  const stockSnapshot = recommendation?.stockSnapshot || {};
  const recommendationId = recommendation?.id;
  const isActioning = actioningId.includes(`-${recommendationId}`);

  return (
    <article className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getPriorityClasses(recommendation?.priority)}`}>
              {formatLabel(recommendation?.priority, 'Priority')}
            </span>
            <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              {formatLabel(recommendation?.status, 'Active')}
            </span>
            {stockSnapshot?.stockStatus ? (
              <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                {formatLabel(stockSnapshot.stockStatus)}
              </span>
            ) : null}
          </div>

          <h3 className="mt-3 text-base font-semibold tracking-tight text-slate-900">
            {getStockItemLabel(stockItem)}
          </h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">{recommendation?.reason || 'Dektal found a possible restock option.'}</p>
        </div>

        <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 lg:min-w-[190px]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Suggested Qty</p>
          <p className="mt-1 text-lg font-semibold text-slate-900">
            {recommendation?.restockQuantitySuggested ?? stockSnapshot?.restockQty ?? '-'}
          </p>
          <p className="mt-1 text-xs text-slate-500">{stockItem?.unit || stockSnapshot?.unit || 'units'}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
        <div className="rounded-[18px] border border-blue-100 bg-blue-50 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700">Suggested Product</p>
          <p className="mt-2 text-base font-semibold text-slate-900">{getProductLabel(product)}</p>
          <p className="mt-1 text-sm text-slate-600">
            {[product?.brand, product?.category, product?.unit, product?.packSize].filter(Boolean).join(' | ') || 'Approved public Supply Hub listing'}
          </p>
          <p className="mt-1 text-sm text-slate-500">{vendor?.displayName || 'Approved vendor'}</p>
        </div>

        <div className="rounded-[18px] border border-slate-200 bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Public Price</p>
          <p className="mt-2 text-base font-semibold text-slate-900">
            {product?.requiresFinanceUnlock
              ? 'Dektal found restock options. Unlock Finance Lock to view pricing.'
              : formatMoney(product?.offerPrice ?? product?.listPrice, product?.currency)}
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-500">No clinic purchase history or order totals are shown.</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={() => product?.id && onOpenProduct(product.id)}
          disabled={!product?.id || isActioning}
          className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3.5 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Eye className="h-3.5 w-3.5" />
          View Product
        </button>

        <button
          type="button"
          onClick={() => onRequestOrder(recommendation)}
          disabled={!canCreateOrder || !product?.id || isActioning}
          title={canCreateOrder ? 'Request this suggested product.' : 'Your role cannot create Supply Hub order requests.'}
          className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ShoppingCart className="h-3.5 w-3.5" />
          Request Order
        </button>

        {canManage ? (
          <>
            <button
              type="button"
              onClick={() => onCreateMapping(recommendation)}
              disabled={!product?.id || !stockItem?.currentStockId || isActioning}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Link2 className="h-3.5 w-3.5" />
              Map Product
            </button>
            {recommendation?.status === 'active' ? (
              <button
                type="button"
                onClick={() => onDismiss(recommendation)}
                disabled={isActioning}
                className="rounded-full border border-rose-200 bg-white px-3.5 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {actioningId === `dismiss-${recommendationId}` ? 'Dismissing...' : 'Dismiss'}
              </button>
            ) : null}
          </>
        ) : (
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-2 text-xs font-semibold text-slate-500">
            Read-only for this role
          </span>
        )}

        <span className="ml-auto text-xs text-slate-400">
          {recommendation?.createdAt ? formatDateTime(recommendation.createdAt) : ''}
        </span>
      </div>
    </article>
  );
}

function MappingCard({ mapping, canManage, actioningId, onOpenProduct, onArchive }) {
  const product = mapping?.supplyHubProduct || null;
  const vendor = product?.vendor || null;
  const stockItem = mapping?.currentStock || {};
  const isActioning = actioningId === `archive-mapping-${mapping?.id}`;

  return (
    <article className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
              {formatLabel(mapping?.mappingStatus, 'Active')}
            </span>
            {mapping?.confidenceScore !== null && mapping?.confidenceScore !== undefined ? (
              <span className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700">
                {Number(mapping.confidenceScore).toFixed(0)}% match
              </span>
            ) : null}
          </div>
          <h3 className="mt-3 text-base font-semibold text-slate-900">{getStockItemLabel(stockItem)}</h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">{mapping?.matchReason || 'Mapped to approved Supply Hub product.'}</p>
        </div>
        <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 md:min-w-[240px]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Mapped Product</p>
          <p className="mt-1 font-semibold text-slate-900">{getProductLabel(product)}</p>
          <p className="mt-1 text-xs text-slate-500">{vendor?.displayName || 'Approved vendor'}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={() => product?.id && onOpenProduct(product.id)}
          disabled={!product?.id}
          className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3.5 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Eye className="h-3.5 w-3.5" />
          View Product
        </button>
        {canManage && mapping?.mappingStatus !== 'archived' ? (
          <button
            type="button"
            onClick={() => onArchive(mapping)}
            disabled={isActioning}
            className="rounded-full border border-rose-200 bg-white px-3.5 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isActioning ? 'Archiving...' : 'Archive Mapping'}
          </button>
        ) : null}
      </div>
    </article>
  );
}

function OrderCard({ order, canManage, actioningId, onOpenOrder, onCancel, onOpenReceive }) {
  const isCancelling = actioningId === `cancel-order-${order?.id}`;
  const receivableItems = getReceivableItems(order);
  const canOpenReceive = canManage && hasMappedReceivableItem(order);

  return (
    <article className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getOrderStatusClasses(order?.orderStatus)}`}>
              {formatLabel(order?.orderStatus, 'Status')}
            </span>
            {order?.orderValuesHidden ? (
              <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                Finance Lock
              </span>
            ) : null}
          </div>
          <h3 className="mt-3 text-base font-semibold tracking-tight text-slate-900">
            {order?.orderNumber || `Order ${order?.id || ''}`}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {order?.vendorDisplayName || 'Approved vendor'} | {getOrderItemCount(order)} item{getOrderItemCount(order) === 1 ? '' : 's'}
          </p>
          <p className="mt-1 text-xs text-slate-400">{formatDateTime(order?.createdAt)}</p>
        </div>

        <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 lg:min-w-[220px]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Order Value</p>
          <p className={`mt-1 text-base font-semibold ${order?.orderValuesHidden ? 'text-amber-700' : 'text-slate-900'}`}>
            {getOrderMoneyText(order)}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={() => onOpenOrder(order.id)}
          className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-blue-700"
        >
          <Eye className="h-3.5 w-3.5" />
          View Details
        </button>

        {canManage && canCancelOrder(order) ? (
          <button
            type="button"
            onClick={() => onCancel(order)}
            disabled={isCancelling}
            className="rounded-full border border-rose-200 bg-white px-3.5 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isCancelling ? 'Cancelling...' : 'Cancel Order'}
          </button>
        ) : null}

        {canReceiveStockForOrder(order) && receivableItems.length ? (
          <button
            type="button"
            disabled={!canOpenReceive}
            onClick={() => onOpenReceive(order)}
            title={canOpenReceive ? 'Receive mapped Supply Hub order items into inventory.' : 'Map this product to inventory before receiving stock.'}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-500"
          >
            <PackageCheck className="h-3.5 w-3.5" />
            Receive Stock
          </button>
        ) : null}
      </div>
    </article>
  );
}

function OrderRequestModal({ orderRequest, submitting, onClose, onChange, onSubmit }) {
  const product = orderRequest?.product || {};
  const unitPrice = getPublicUnitPrice(product);
  const quantity = Number(orderRequest?.quantity || 0);
  const previewTotal = unitPrice !== null && Number.isFinite(quantity) && quantity > 0
    ? unitPrice * quantity
    : null;

  return (
    <ModalShell eyebrow="Supply Hub Order Request" title="Request Order" onClose={onClose}>
      <form className="space-y-5" onSubmit={onSubmit}>
        <section className="rounded-[18px] border border-blue-100 bg-blue-50 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700">Selected Product</p>
          <h3 className="mt-2 text-lg font-semibold text-slate-900">{product?.name || 'Supply Hub product'}</h3>
          <p className="mt-1 text-sm text-slate-600">
            {[product?.brand, product?.category, product?.unit, product?.packSize].filter(Boolean).join(' | ') || 'Approved marketplace product'}
          </p>
          <p className="mt-1 text-sm text-slate-500">{getVendorNameFromProduct(product)}</p>
        </section>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Quantity</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={orderRequest?.quantity}
              onChange={(event) => onChange({ quantity: event.target.value })}
              className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
              required
            />
          </label>

          <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Public Unit Price</p>
            <p className="mt-1 font-semibold text-slate-900">
              {unitPrice === null ? 'Price not listed' : formatMoney(unitPrice, product?.currency)}
            </p>
          </div>

          <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Linked Stock Item</p>
            <p className="mt-1 font-semibold text-slate-900">{orderRequest?.stockLabel || 'Not linked'}</p>
          </div>

          <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Estimated Total</p>
            <p className="mt-1 font-semibold text-slate-900">
              {previewTotal === null ? 'Calculated by backend after submit' : formatMoney(previewTotal, product?.currency)}
            </p>
          </div>
        </div>

        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Notes</span>
          <textarea
            value={orderRequest?.notes}
            onChange={(event) => onChange({ notes: event.target.value })}
            maxLength={ORDER_NOTES_MAX_LENGTH}
            rows={4}
            className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
            placeholder="Optional order note for this clinic request"
          />
          <span className="mt-1 block text-xs text-slate-400">
            {String(orderRequest?.notes || '').length}/{ORDER_NOTES_MAX_LENGTH}
          </span>
        </label>

        <section className="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm font-semibold text-amber-900">This creates an order request only.</p>
          <p className="mt-1 text-sm leading-6 text-amber-800">
            The backend calculates the final total. This action does not receive stock, modify inventory quantities, or create stock ledger movements.
          </p>
        </section>

        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
            {submitting ? 'Sending Request...' : 'Send Order Request'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ReceiveStockModal({ receiveRequest, submitting, onClose, onChange, onSubmit, onOpenMappings }) {
  const item = receiveRequest?.item || {};
  const remainingQuantity = getRemainingReceiveQuantity(item);
  const isFullOrderReceipt = receiveRequest?.mode === 'order';

  return (
    <ModalShell eyebrow="Supply Hub Receipt" title="Receive Stock" onClose={onClose}>
      <form className="space-y-5" onSubmit={onSubmit}>
        <section className="rounded-[18px] border border-emerald-100 bg-emerald-50 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Mapped Order Item</p>
          <h3 className="mt-2 text-lg font-semibold text-slate-900">{item?.productNameSnapshot || 'Supply Hub product'}</h3>
          <p className="mt-1 text-sm text-slate-600">
            {receiveRequest?.order?.orderNumber || 'Supply Hub order'} | {receiveRequest?.order?.vendorDisplayName || item?.vendorNameSnapshot || 'Approved vendor'}
          </p>
        </section>

        {!item?.currentStockId ? (
          <section className="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900">Inventory mapping required.</p>
            <p className="mt-1 text-sm leading-6 text-amber-800">
              Map this product to inventory before receiving stock. The frontend will not guess a stock item from product names.
            </p>
            <button
              type="button"
              onClick={onOpenMappings}
              className="mt-3 inline-flex rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Open Product Mappings
            </button>
          </section>
        ) : null}

        <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Ordered</p>
            <p className="mt-1 font-semibold text-slate-900">{item?.quantity || '0'}</p>
          </div>
          <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Already Received</p>
            <p className="mt-1 font-semibold text-slate-900">{item?.receivedQuantity || '0'}</p>
          </div>
          <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Remaining</p>
            <p className="mt-1 font-semibold text-slate-900">{remainingQuantity.toFixed(2)}</p>
          </div>
        </section>

        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Receive Quantity</span>
          <input
            type="number"
            min="0.01"
            max={remainingQuantity || undefined}
            step="0.01"
            value={receiveRequest?.receivedQuantity}
            onChange={(event) => onChange({ receivedQuantity: event.target.value })}
            disabled={!item?.currentStockId || isFullOrderReceipt}
            className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-200 focus:bg-white focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            required
          />
          {isFullOrderReceipt ? (
            <span className="mt-1 block text-xs text-slate-400">
              Full order receipt receives each mapped item's remaining quantity on the backend.
            </span>
          ) : null}
        </label>

        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Receipt Notes</span>
          <textarea
            value={receiveRequest?.notes}
            onChange={(event) => onChange({ notes: event.target.value })}
            maxLength={RECEIVE_NOTES_MAX_LENGTH}
            rows={3}
            className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-200 focus:bg-white focus:ring-2 focus:ring-emerald-100"
            placeholder="Optional stock receipt note"
          />
          <span className="mt-1 block text-xs text-slate-400">
            {String(receiveRequest?.notes || '').length}/{RECEIVE_NOTES_MAX_LENGTH}
          </span>
        </label>

        <section className="rounded-[18px] border border-blue-100 bg-blue-50 px-4 py-3">
          <p className="text-sm font-semibold text-blue-900">Stock ledger protection is active.</p>
          <p className="mt-1 text-sm leading-6 text-blue-800">
            Stock is updated only after backend confirmation, and the backend creates an append-only stock ledger movement for this receipt.
          </p>
        </section>

        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !item?.currentStockId}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />}
            {submitting ? 'Receiving...' : isFullOrderReceipt ? 'Receive Full Order' : 'Receive Stock'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function AdminVendorCard({ vendor, actioningId, onEdit, onApprove, onReject, onSuspend }) {
  const isActioning = actioningId && actioningId.includes(`vendor-${vendor?.id}`);

  return (
    <article className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getApprovalStatusClasses(vendor?.verificationStatus)}`}>
              {formatLabel(vendor?.verificationStatus, 'Status')}
            </span>
            {vendor?.sponsoredStatus && vendor.sponsoredStatus !== 'none' ? (
              <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getSponsoredClasses(vendor.sponsoredStatus)}`}>
                {formatLabel(vendor.sponsoredStatus)}
              </span>
            ) : null}
          </div>
          <h3 className="mt-3 text-base font-semibold tracking-tight text-slate-900">{vendor?.displayName || 'Vendor'}</h3>
          <p className="mt-1 text-sm text-slate-500">{vendor?.legalName || 'No legal name added'}</p>
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">
            {vendor?.publicDescription || 'No public description added yet.'}
          </p>
          <p className="mt-2 text-xs text-slate-400">
            Service areas: {listToCsv(vendor?.serviceAreas) || 'Not specified'}
          </p>
        </div>
        <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 lg:min-w-[260px]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Admin Contact</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{vendor?.contactEmail || 'No email'}</p>
          <p className="mt-1 text-sm text-slate-600">{vendor?.contactPhone || 'No phone'}</p>
          <p className="mt-1 text-xs text-slate-400">{vendor?.websiteUrl || 'No website'}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={() => onEdit(vendor)}
          className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700"
        >
          Edit Vendor
        </button>
        <button
          type="button"
          onClick={() => onApprove(vendor)}
          disabled={isActioning || vendor?.verificationStatus === 'approved'}
          className="rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Approve
        </button>
        <button
          type="button"
          onClick={() => onReject(vendor)}
          disabled={isActioning}
          className="rounded-full border border-rose-200 bg-white px-3.5 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reject
        </button>
        <button
          type="button"
          onClick={() => onSuspend(vendor)}
          disabled={isActioning}
          className="rounded-full border border-slate-300 bg-slate-100 px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Suspend
        </button>
      </div>
    </article>
  );
}

function AdminProductCard({ product, actioningId, onEdit, onApprove, onReject, onSponsor, onUnsponsor }) {
  const isActioning = actioningId && actioningId.includes(`product-${product?.id}`);

  return (
    <article className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getApprovalStatusClasses(product?.approvalStatus)}`}>
              {formatLabel(product?.approvalStatus, 'Approval')}
            </span>
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getAvailabilityClasses(product?.availabilityStatus)}`}>
              {formatLabel(product?.availabilityStatus, 'Availability')}
            </span>
            {product?.sponsoredStatus && product.sponsoredStatus !== 'none' ? (
              <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getSponsoredClasses(product.sponsoredStatus)}`}>
                {formatLabel(product.sponsoredStatus)}
              </span>
            ) : null}
          </div>
          <h3 className="mt-3 text-base font-semibold tracking-tight text-slate-900">{product?.name || 'Product'}</h3>
          <p className="mt-1 text-sm text-slate-500">
            {[product?.brand, product?.category, product?.unit, product?.packSize].filter(Boolean).join(' | ') || 'Product details not added'}
          </p>
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">
            {product?.description || 'No description added yet.'}
          </p>
        </div>
        <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 lg:min-w-[250px]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Marketplace Price</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">
            {formatMoney(product?.offerPrice ?? product?.listPrice, product?.currency)}
          </p>
          <p className="mt-1 text-xs text-slate-500">{product?.vendor?.displayName || `Vendor ${product?.vendorId || ''}`}</p>
          {product?.sponsoredRank ? (
            <p className="mt-1 text-xs text-blue-600">Sponsored rank {product.sponsoredRank}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={() => onEdit(product)}
          className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700"
        >
          Edit Product
        </button>
        <button
          type="button"
          onClick={() => onApprove(product)}
          disabled={isActioning || product?.approvalStatus === 'approved'}
          className="rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Approve
        </button>
        <button
          type="button"
          onClick={() => onReject(product)}
          disabled={isActioning}
          className="rounded-full border border-rose-200 bg-white px-3.5 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reject
        </button>
        <button
          type="button"
          onClick={() => onSponsor(product)}
          disabled={isActioning || product?.approvalStatus !== 'approved'}
          className="rounded-full border border-blue-200 bg-blue-50 px-3.5 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Sponsor
        </button>
        <button
          type="button"
          onClick={() => onUnsponsor(product)}
          disabled={isActioning || !product?.sponsoredStatus || product.sponsoredStatus === 'none'}
          className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Unsponsor
        </button>
      </div>
    </article>
  );
}

function AdminVendorModal({ mode, form, submitting, onClose, onChange, onSubmit }) {
  return (
    <ModalShell eyebrow="Supply Hub Admin" title={mode === 'edit' ? 'Edit Vendor' : 'Create Vendor'} onClose={onClose}>
      <form className="space-y-5" onSubmit={onSubmit}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Display Name</span>
            <input value={form.displayName} onChange={(event) => onChange({ displayName: event.target.value })} required className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Legal Name</span>
            <input value={form.legalName} onChange={(event) => onChange({ legalName: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Contact Email</span>
            <input type="email" value={form.contactEmail} onChange={(event) => onChange({ contactEmail: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Contact Phone</span>
            <input value={form.contactPhone} onChange={(event) => onChange({ contactPhone: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Service Areas</span>
            <input value={form.serviceAreas} onChange={(event) => onChange({ serviceAreas: event.target.value })} placeholder="Mumbai, Pune" className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Website URL</span>
            <input value={form.websiteUrl} onChange={(event) => onChange({ websiteUrl: event.target.value })} placeholder="https://example.com" className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">GST Number</span>
            <input value={form.gstNumber} onChange={(event) => onChange({ gstNumber: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Sponsored Status</span>
            <select value={form.sponsoredStatus} onChange={(event) => onChange({ sponsoredStatus: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100">
              <option value="none">None</option>
              <option value="sponsored">Sponsored</option>
              <option value="featured">Featured</option>
            </select>
          </label>
          <label className="md:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Logo URL</span>
            <input value={form.logoUrl} onChange={(event) => onChange({ logoUrl: event.target.value })} placeholder="https://example.com/logo.png" className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label className="md:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Public Description</span>
            <textarea value={form.publicDescription} onChange={(event) => onChange({ publicDescription: event.target.value })} rows={4} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
        </div>
        <AdminPrivacyNotice />
        <ModalActions submitting={submitting} submitLabel={mode === 'edit' ? 'Save Vendor' : 'Create Vendor'} onClose={onClose} />
      </form>
    </ModalShell>
  );
}

function AdminProductModal({ mode, form, vendors, submitting, onClose, onChange, onSubmit }) {
  return (
    <ModalShell eyebrow="Supply Hub Admin" title={mode === 'edit' ? 'Edit Product' : 'Create Product'} onClose={onClose}>
      <form className="space-y-5" onSubmit={onSubmit}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Vendor</span>
            <select value={form.vendorId} onChange={(event) => onChange({ vendorId: event.target.value })} required className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100">
              <option value="">Select vendor</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>{vendor.displayName}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Product Name</span>
            <input value={form.name} onChange={(event) => onChange({ name: event.target.value })} required className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Category</span>
            <input value={form.category} onChange={(event) => onChange({ category: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Brand</span>
            <input value={form.brand} onChange={(event) => onChange({ brand: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Unit</span>
            <input value={form.unit} onChange={(event) => onChange({ unit: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Pack Size</span>
            <input value={form.packSize} onChange={(event) => onChange({ packSize: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">List Price</span>
            <input type="number" min="0" step="0.01" value={form.listPrice} onChange={(event) => onChange({ listPrice: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Offer Price</span>
            <input type="number" min="0" step="0.01" value={form.offerPrice} onChange={(event) => onChange({ offerPrice: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Currency</span>
            <input value={form.currency} onChange={(event) => onChange({ currency: event.target.value.toUpperCase() })} maxLength={3} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Availability</span>
            <select value={form.availabilityStatus} onChange={(event) => onChange({ availabilityStatus: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100">
              <option value="available">Available</option>
              <option value="out_of_stock">Out of stock</option>
              <option value="discontinued">Discontinued</option>
              <option value="hidden">Hidden</option>
            </select>
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Sponsored Status</span>
            <select value={form.sponsoredStatus} onChange={(event) => onChange({ sponsoredStatus: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100">
              <option value="none">None</option>
              <option value="sponsored">Sponsored</option>
              <option value="featured">Featured</option>
            </select>
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Sponsored Rank</span>
            <input type="number" min="1" step="1" value={form.sponsoredRank} onChange={(event) => onChange({ sponsoredRank: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label className="md:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Image URL</span>
            <input value={form.imageUrl} onChange={(event) => onChange({ imageUrl: event.target.value })} placeholder="https://example.com/product.png" className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label className="md:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Tags</span>
            <input value={form.tags} onChange={(event) => onChange({ tags: event.target.value })} placeholder="composite, resin, restorative" className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
          <label className="md:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Description</span>
            <textarea value={form.description} onChange={(event) => onChange({ description: event.target.value })} rows={4} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
        </div>
        <AdminPrivacyNotice />
        <ModalActions submitting={submitting} submitLabel={mode === 'edit' ? 'Save Product' : 'Create Product'} onClose={onClose} />
      </form>
    </ModalShell>
  );
}

function AdminReasonModal({ title, label, reason, submitting, onChange, onClose, onSubmit }) {
  return (
    <ModalShell eyebrow="Supply Hub Admin" title={title} onClose={onClose}>
      <form className="space-y-5" onSubmit={onSubmit}>
        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</span>
          <textarea value={reason} onChange={(event) => onChange(event.target.value)} rows={4} required className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
        </label>
        <AdminPrivacyNotice />
        <ModalActions submitting={submitting} submitLabel="Submit" onClose={onClose} />
      </form>
    </ModalShell>
  );
}

function SponsorProductModal({ product, sponsorRequest, submitting, onChange, onClose, onSubmit }) {
  return (
    <ModalShell eyebrow="Supply Hub Admin" title="Sponsor Product" onClose={onClose}>
      <form className="space-y-5" onSubmit={onSubmit}>
        <section className="rounded-[18px] border border-blue-100 bg-blue-50 p-4">
          <p className="text-sm font-semibold text-blue-900">{product?.name || 'Supply Hub product'}</p>
          <p className="mt-1 text-sm text-blue-800">Sponsored listings remain marketplace records only and do not expose clinic stock or order data.</p>
        </section>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Sponsored Status</span>
            <select value={sponsorRequest.sponsoredStatus} onChange={(event) => onChange({ sponsoredStatus: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100">
              <option value="sponsored">Sponsored</option>
              <option value="featured">Featured</option>
            </select>
          </label>
          <label>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Sponsored Rank</span>
            <input type="number" min="1" step="1" value={sponsorRequest.sponsoredRank} onChange={(event) => onChange({ sponsoredRank: event.target.value })} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
          </label>
        </div>
        <ModalActions submitting={submitting} submitLabel="Sponsor Product" onClose={onClose} />
      </form>
    </ModalShell>
  );
}

function AdminPrivacyNotice() {
  return (
    <section className="rounded-[18px] border border-cyan-100 bg-cyan-50 px-4 py-3">
      <p className="text-sm font-semibold text-cyan-900">Marketplace admin data only.</p>
      <p className="mt-1 text-sm leading-6 text-cyan-800">
        This screen does not read clinic inventory, stock levels, order history, recommendations, mappings, patient data, or clinic purchase records.
      </p>
    </section>
  );
}

function ModalActions({ submitting, submitLabel, onClose }) {
  return (
    <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 pt-4">
      <button
        type="button"
        onClick={onClose}
        disabled={submitting}
        className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
        {submitting ? 'Saving...' : submitLabel}
      </button>
    </div>
  );
}

function OrderDetailModal({ order, canManage, actioningId, onClose, onCancel, onOpenReceiveItem, onOpenReceiveOrder, onOpenMappings }) {
  const receivableItems = getReceivableItems(order);
  const canReceiveFullOrder = canManage && receivableItems.length > 0 && receivableItems.every((item) => item.currentStockId);

  return (
    <ModalShell
      eyebrow="Supply Hub Order"
      title={order?.orderNumber || 'Order Details'}
      onClose={onClose}
    >
      <div className="space-y-5">
        <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Status</p>
            <span className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getOrderStatusClasses(order?.orderStatus)}`}>
              {formatLabel(order?.orderStatus)}
            </span>
          </div>
          <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Vendor</p>
            <p className="mt-1 font-semibold text-slate-900">{order?.vendorDisplayName || 'Approved vendor'}</p>
          </div>
          <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Created</p>
            <p className="mt-1 font-semibold text-slate-900">{formatDateTime(order?.createdAt)}</p>
          </div>
        </section>

        {order?.orderValuesHidden ? (
          <section className="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900">Order values are protected.</p>
            <p className="mt-1 text-sm leading-6 text-amber-800">
              {order?.financeLock?.message || 'Unlock Finance Lock to view order values.'}
            </p>
          </section>
        ) : null}

        <section className="rounded-[18px] border border-slate-200 bg-white p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Items</p>
              <p className="mt-1 text-sm text-slate-500">{getOrderItemCount(order)} item{getOrderItemCount(order) === 1 ? '' : 's'}</p>
            </div>
            <div className="rounded-[14px] border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Total</p>
              <p className={`mt-1 font-semibold ${order?.orderValuesHidden ? 'text-amber-700' : 'text-slate-900'}`}>
                {getOrderMoneyText(order)}
              </p>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto rounded-[16px] border border-slate-200">
            <div className="grid min-w-[980px] grid-cols-[minmax(0,1.5fr)_80px_90px_90px_110px_120px_110px] bg-slate-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              <span>Product</span>
              <span>Qty</span>
              <span>Received</span>
              <span>Remaining</span>
              <span>Unit Price</span>
              <span>Total</span>
              <span>Action</span>
            </div>
            {(order?.items || []).map((item) => {
              const remainingQuantity = getRemainingReceiveQuantity(item);
              const itemCanReceive = canManage && canReceiveStockForOrder(order) && remainingQuantity > 0 && item.currentStockId;
              return (
                <div key={item.id} className="grid min-w-[980px] grid-cols-[minmax(0,1.5fr)_80px_90px_90px_110px_120px_110px] items-center border-t border-slate-100 px-4 py-3 text-sm text-slate-700">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900">{item.productNameSnapshot || 'Product snapshot'}</p>
                    {!item.currentStockId && canReceiveStockForOrder(order) && remainingQuantity > 0 ? (
                      <button
                        type="button"
                        onClick={onOpenMappings}
                        className="mt-1 text-left text-xs font-semibold text-amber-700 transition hover:text-amber-800"
                      >
                        Map this product to inventory before receiving stock.
                      </button>
                    ) : null}
                  </div>
                  <span>{item.quantity}</span>
                  <span>{item.receivedQuantity || '0'}</span>
                  <span>{remainingQuantity.toFixed(2)}</span>
                  <span>{order?.orderValuesHidden ? 'Locked' : formatMoney(item.unitPrice, item.currency)}</span>
                  <span>{order?.orderValuesHidden ? 'Locked' : formatMoney(item.totalPrice, item.currency)}</span>
                  <span>
                    {canReceiveStockForOrder(order) && remainingQuantity > 0 ? (
                      <button
                        type="button"
                        onClick={() => onOpenReceiveItem(order, item)}
                        disabled={!itemCanReceive}
                        title={item.currentStockId ? 'Receive this mapped item.' : 'Map this product to inventory before receiving stock.'}
                        className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-500"
                      >
                        Receive
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400">Done</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-[18px] border border-slate-200 bg-slate-50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Notes</p>
            <p className="mt-2 text-sm leading-6 text-slate-700">{order?.notes || 'No notes added.'}</p>
          </div>
          <div className="rounded-[18px] border border-slate-200 bg-slate-50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cancellation</p>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              {order?.orderStatus === 'cancelled'
                ? order?.cancellationReason || 'Cancelled without a reason.'
                : 'Not cancelled.'}
            </p>
          </div>
        </section>

        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 pt-4">
          {canReceiveStockForOrder(order) && receivableItems.length ? (
            <button
              type="button"
              disabled={!canReceiveFullOrder}
              onClick={() => onOpenReceiveOrder(order)}
              title={canReceiveFullOrder ? 'Receive all remaining mapped items.' : 'Map every product to inventory before receiving the full order.'}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-500"
            >
              <PackageCheck className="h-4 w-4" />
              Receive Full Order
            </button>
          ) : null}

          {canManage && canCancelOrder(order) ? (
            <button
              type="button"
              onClick={() => onCancel(order)}
              disabled={actioningId === `cancel-order-${order?.id}`}
              className="rounded-full border border-rose-200 bg-white px-4 py-2.5 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {actioningId === `cancel-order-${order?.id}` ? 'Cancelling...' : 'Cancel Order'}
            </button>
          ) : null}
        </div>
      </div>
    </ModalShell>
  );
}

function StatusMessage({ type = 'info', title, message, actionLabel, onAction }) {
  const isError = type === 'error';
  return (
    <div className={`rounded-[20px] border px-5 py-5 ${isError ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-start gap-3">
        {isError ? (
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
        ) : (
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
        )}
        <div>
          <p className={`font-semibold ${isError ? 'text-rose-800' : 'text-slate-900'}`}>{title}</p>
          <p className={`mt-1 text-sm leading-6 ${isError ? 'text-rose-700' : 'text-slate-600'}`}>{message}</p>
          {onAction ? (
            <button
              type="button"
              onClick={onAction}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              <RefreshCw className="h-4 w-4" />
              {actionLabel || 'Retry'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function SupplyHub() {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const roleKey = normalizeRole(user?.role);
  const canBrowseMarketplace = canBrowseSupplyHubMarketplace(roleKey);
  const canViewRecommendations = canViewSupplyHubRecommendations(roleKey);
  const canManageRecommendations = canManageSupplyHubRecommendations(roleKey);
  const canCreateOrders = canCreateSupplyHubOrders(roleKey);
  const canViewOrders = canViewSupplyHubOrders(roleKey);
  const canManageOrders = canManageSupplyHubOrders(roleKey);
  const isPlatformAdmin = roleKey === ROLE_KEYS.PLATFORM_ADMIN;

  const [activeTab, setActiveTab] = useState(SUPPLY_HUB_TABS.MARKETPLACE);
  const [productFilters, setProductFilters] = useState(EMPTY_PRODUCT_FILTERS);
  const [vendorFilters, setVendorFilters] = useState(EMPTY_VENDOR_FILTERS);
  const [adminVendorFilters, setAdminVendorFilters] = useState(EMPTY_ADMIN_VENDOR_FILTERS);
  const [adminProductFilters, setAdminProductFilters] = useState(EMPTY_ADMIN_PRODUCT_FILTERS);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [adminVendors, setAdminVendors] = useState([]);
  const [adminProducts, setAdminProducts] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const [vendorCount, setVendorCount] = useState(0);
  const [adminVendorCount, setAdminVendorCount] = useState(0);
  const [adminProductCount, setAdminProductCount] = useState(0);
  const [recommendationCount, setRecommendationCount] = useState(0);
  const [mappingCount, setMappingCount] = useState(0);
  const [orderCount, setOrderCount] = useState(0);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingAdminVendors, setLoadingAdminVendors] = useState(false);
  const [loadingAdminProducts, setLoadingAdminProducts] = useState(false);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [generatingRecommendations, setGeneratingRecommendations] = useState(false);
  const [productError, setProductError] = useState('');
  const [vendorError, setVendorError] = useState('');
  const [adminVendorError, setAdminVendorError] = useState('');
  const [adminProductError, setAdminProductError] = useState('');
  const [recommendationError, setRecommendationError] = useState('');
  const [mappingError, setMappingError] = useState('');
  const [orderError, setOrderError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderRequest, setOrderRequest] = useState(null);
  const [receiveRequest, setReceiveRequest] = useState(null);
  const [vendorAdminModal, setVendorAdminModal] = useState(null);
  const [productAdminModal, setProductAdminModal] = useState(null);
  const [adminReasonRequest, setAdminReasonRequest] = useState(null);
  const [sponsorRequest, setSponsorRequest] = useState(null);
  const [detailLoading, setDetailLoading] = useState('');
  const [recommendationActionId, setRecommendationActionId] = useState('');
  const [mappingActionId, setMappingActionId] = useState('');
  const [orderActionId, setOrderActionId] = useState('');
  const [adminActionId, setAdminActionId] = useState('');
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [submittingReceive, setSubmittingReceive] = useState(false);
  const [submittingAdmin, setSubmittingAdmin] = useState(false);

  const vendorOptions = useMemo(
    () => vendors.map((vendor) => ({ id: vendor.id, name: vendor.displayName || `Vendor ${vendor.id}` })),
    [vendors]
  );

  const updateProductFilter = (field, value) => {
    setProductFilters((current) => ({ ...current, [field]: value }));
  };

  const updateVendorFilter = (field, value) => {
    setVendorFilters((current) => ({ ...current, [field]: value }));
  };

  const updateAdminVendorFilter = (field, value) => {
    setAdminVendorFilters((current) => ({ ...current, [field]: value }));
  };

  const updateAdminProductFilter = (field, value) => {
    setAdminProductFilters((current) => ({ ...current, [field]: value }));
  };

  const loadProducts = useCallback(async () => {
    if (!canBrowseMarketplace) return;
    setLoadingProducts(true);
    setProductError('');
    try {
      const result = await getSupplyHubProducts({
        ...productFilters,
        limit: DEFAULT_PRODUCTS_LIMIT,
      });
      setProducts(result.rows);
      setProductCount(result.count);
    } catch (error) {
      console.error('Supply Hub products failed:', error);
      setProductError(getFriendlySupplyHubError(error));
    } finally {
      setLoadingProducts(false);
    }
  }, [canBrowseMarketplace, productFilters]);

  const loadVendors = useCallback(async () => {
    if (!canBrowseMarketplace) return;
    setLoadingVendors(true);
    setVendorError('');
    try {
      const result = await getSupplyHubVendors({
        ...vendorFilters,
        limit: DEFAULT_VENDORS_LIMIT,
      });
      setVendors(result.rows);
      setVendorCount(result.count);
    } catch (error) {
      console.error('Supply Hub vendors failed:', error);
      setVendorError(getFriendlySupplyHubError(error));
    } finally {
      setLoadingVendors(false);
    }
  }, [canBrowseMarketplace, vendorFilters]);

  const loadAdminVendors = useCallback(async () => {
    if (!isPlatformAdmin) return;
    setLoadingAdminVendors(true);
    setAdminVendorError('');
    try {
      const result = await getAdminSupplyHubVendors({
        ...adminVendorFilters,
        limit: DEFAULT_VENDORS_LIMIT,
      });
      setAdminVendors(result.rows);
      setAdminVendorCount(result.count);
    } catch (error) {
      console.error('Supply Hub admin vendors failed:', error);
      setAdminVendorError(getFriendlySupplyHubError(error));
    } finally {
      setLoadingAdminVendors(false);
    }
  }, [adminVendorFilters, isPlatformAdmin]);

  const loadAdminProducts = useCallback(async () => {
    if (!isPlatformAdmin) return;
    setLoadingAdminProducts(true);
    setAdminProductError('');
    try {
      const result = await getAdminSupplyHubProducts({
        ...adminProductFilters,
        limit: DEFAULT_PRODUCTS_LIMIT,
      });
      setAdminProducts(result.rows);
      setAdminProductCount(result.count);
    } catch (error) {
      console.error('Supply Hub admin products failed:', error);
      setAdminProductError(getFriendlySupplyHubError(error));
    } finally {
      setLoadingAdminProducts(false);
    }
  }, [adminProductFilters, isPlatformAdmin]);

  const loadRecommendations = useCallback(async () => {
    if (!canViewRecommendations) return;
    setLoadingRecommendations(true);
    setRecommendationError('');
    try {
      const result = await getSupplyHubRecommendations({
        status: 'active',
        limit: DEFAULT_RECOMMENDATIONS_LIMIT,
      });
      setRecommendations(result.rows);
      setRecommendationCount(result.count);
    } catch (error) {
      console.error('Supply Hub recommendations failed:', error);
      setRecommendationError(getFriendlySupplyHubError(error));
    } finally {
      setLoadingRecommendations(false);
    }
  }, [canViewRecommendations]);

  const loadMappings = useCallback(async () => {
    if (!canManageRecommendations) return;
    setLoadingMappings(true);
    setMappingError('');
    try {
      const result = await getSupplyHubProductMappings({
        limit: DEFAULT_MAPPINGS_LIMIT,
      });
      setMappings(result.rows);
      setMappingCount(result.count);
    } catch (error) {
      console.error('Supply Hub product mappings failed:', error);
      setMappingError(getFriendlySupplyHubError(error));
    } finally {
      setLoadingMappings(false);
    }
  }, [canManageRecommendations]);

  const loadOrders = useCallback(async () => {
    if (!canViewOrders) return;
    setLoadingOrders(true);
    setOrderError('');
    try {
      const result = await getSupplyHubOrders({
        limit: DEFAULT_ORDERS_LIMIT,
      });
      setOrders(result.rows);
      setOrderCount(result.count);
    } catch (error) {
      console.error('Supply Hub orders failed:', error);
      setOrderError(getFriendlySupplyHubError(error));
    } finally {
      setLoadingOrders(false);
    }
  }, [canViewOrders]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    if (!canViewRecommendations) return;
    loadRecommendations();
  }, [canViewRecommendations, loadRecommendations]);

  useEffect(() => {
    if (!canManageRecommendations) return;
    loadMappings();
  }, [canManageRecommendations, loadMappings]);

  useEffect(() => {
    if (!canViewOrders) return;
    loadOrders();
  }, [canViewOrders, loadOrders]);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    loadAdminVendors();
  }, [isPlatformAdmin, loadAdminVendors]);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    loadAdminProducts();
  }, [isPlatformAdmin, loadAdminProducts]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const tab = params.get('tab');
    const source = params.get('source') || location.state?.source;
    if (tab === SUPPLY_HUB_TABS.ADMIN) {
      setActiveTab(SUPPLY_HUB_TABS.ADMIN);
    } else if (
      tab === SUPPLY_HUB_TABS.ORDERS
      || source === 'supply_hub_order_created'
      || source === 'supply_hub_order_cancelled'
      || source === 'supply_hub_stock_received'
    ) {
      setActiveTab(SUPPLY_HUB_TABS.ORDERS);
    } else if (source === 'supply_hub_restock_suggestion') {
      setActiveTab(SUPPLY_HUB_TABS.RESTOCK);
    }
  }, [location.search, location.state]);

  const refreshMarketplace = () => {
    loadProducts();
    loadVendors();
  };

  const refreshRecommendationsAndMappings = () => {
    loadRecommendations();
    loadMappings();
  };

  const refreshOrders = () => {
    loadOrders();
  };

  const refreshAdminMarketplace = () => {
    loadAdminVendors();
    loadAdminProducts();
  };

  const openProductDetail = async (productId) => {
    setDetailLoading(`product-${productId}`);
    try {
      const product = await getSupplyHubProductById(productId);
      setSelectedProduct(product);
    } catch (error) {
      console.error('Supply Hub product detail failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setDetailLoading('');
    }
  };

  const openVendorDetail = async (vendorId) => {
    setDetailLoading(`vendor-${vendorId}`);
    try {
      const vendor = await getSupplyHubVendorById(vendorId);
      setSelectedVendor(vendor);
    } catch (error) {
      console.error('Supply Hub vendor detail failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setDetailLoading('');
    }
  };

  const showPlaceholder = (message) => {
    toast(message, { icon: 'i' });
  };

  const openOrderRequestFromProduct = (product, extras = {}) => {
    if (!canCreateOrders) {
      toast.error('Your role cannot create Supply Hub order requests.');
      return;
    }

    const vendorId = getVendorIdFromProduct(product);
    if (!product?.id || !vendorId) {
      toast.error('This product does not have enough approved vendor information for an order request.');
      return;
    }

    setOrderRequest({
      product,
      vendorId,
      quantity: extras.quantity || '1',
      notes: '',
      currentStockId: extras.currentStockId || null,
      stockLabel: extras.stockLabel || '',
      sourceRecommendationId: extras.sourceRecommendationId || null,
    });
  };

  const openOrderRequestFromRecommendation = (recommendation) => {
    const product = recommendation?.supplyHubProduct;
    const currentStockId = recommendation?.currentStock?.currentStockId || null;
    const stockLabel = getStockItemLabel(recommendation?.currentStock || {});
    const suggestedQuantity = recommendation?.restockQuantitySuggested
      ?? recommendation?.stockSnapshot?.restockQty
      ?? '1';

    openOrderRequestFromProduct(product, {
      currentStockId,
      stockLabel,
      quantity: suggestedQuantity,
      sourceRecommendationId: recommendation?.id || null,
    });
  };

  const updateOrderRequest = (patch) => {
    setOrderRequest((current) => (current ? { ...current, ...patch } : current));
  };

  const handleSubmitOrderRequest = async (event) => {
    event.preventDefault();
    if (!orderRequest || submittingOrder) return;

    const quantity = Number(orderRequest.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error('Quantity must be greater than 0.');
      return;
    }

    if (!orderRequest.product?.id || !orderRequest.vendorId) {
      toast.error('Product and vendor are required for order request.');
      return;
    }

    if (String(orderRequest.notes || '').length > ORDER_NOTES_MAX_LENGTH) {
      toast.error(`Notes must be ${ORDER_NOTES_MAX_LENGTH} characters or fewer.`);
      return;
    }

    setSubmittingOrder(true);
    try {
      const payload = {
        vendorId: orderRequest.vendorId,
        notes: orderRequest.notes || '',
        items: [
          {
            productId: orderRequest.product.id,
            quantity,
            ...(orderRequest.currentStockId ? { currentStockId: orderRequest.currentStockId } : {}),
          },
        ],
      };
      const createdOrder = await createSupplyHubOrder(payload);
      toast.success('Supply Hub order request created safely. Stock quantities were not changed.');
      setOrderRequest(null);
      setSelectedProduct(null);
      setActiveTab(SUPPLY_HUB_TABS.ORDERS);
      await loadOrders();
      if (createdOrder) {
        setSelectedOrder(createdOrder);
      }
    } catch (error) {
      console.error('Supply Hub order request failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setSubmittingOrder(false);
    }
  };

  const openOrderDetail = async (orderId) => {
    if (!orderId) return;
    setDetailLoading(`order-${orderId}`);
    try {
      const order = await getSupplyHubOrderById(orderId);
      setSelectedOrder(order);
    } catch (error) {
      console.error('Supply Hub order detail failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setDetailLoading('');
    }
  };

  const handleCancelOrder = async (order) => {
    if (!order?.id || !canManageOrders) return;
    const confirmed = window.confirm('Cancel this Supply Hub order request? This will not delete the order or modify stock.');
    if (!confirmed) return;

    const cancellationReason = window.prompt('Optional cancellation reason:') || '';
    setOrderActionId(`cancel-order-${order.id}`);
    try {
      const cancelledOrder = await cancelSupplyHubOrder(order.id, { cancellationReason });
      toast.success('Supply Hub order cancelled. No stock quantities were changed.');
      await loadOrders();
      if (selectedOrder?.id === order.id) {
        setSelectedOrder(cancelledOrder);
      }
    } catch (error) {
      console.error('Supply Hub order cancel failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setOrderActionId('');
    }
  };

  const openProductMappingsForReceive = () => {
    setReceiveRequest(null);
    setSelectedOrder(null);
    setActiveTab(SUPPLY_HUB_TABS.MAPPINGS);
    toast('Map this Supply Hub product to a tenant-owned inventory item before receiving stock.');
  };

  const openReceiveStockForItem = (order, item) => {
    if (!canManageOrders) {
      toast.error('Your role cannot receive Supply Hub stock.');
      return;
    }

    if (!item?.currentStockId) {
      toast.error('Map this product to inventory before receiving stock.');
      return;
    }

    const remainingQuantity = getRemainingReceiveQuantity(item);
    if (remainingQuantity <= 0) {
      toast('This item is already fully received.');
      return;
    }

    setReceiveRequest({
      mode: 'item',
      order,
      item,
      receivedQuantity: remainingQuantity.toFixed(2),
      notes: '',
    });
  };

  const openReceiveStockForOrder = (order) => {
    if (!canManageOrders) {
      toast.error('Your role cannot receive Supply Hub stock.');
      return;
    }

    const receivableItems = getReceivableItems(order);
    if (!receivableItems.length) {
      toast('This order has no remaining items to receive.');
      return;
    }

    const missingMappedItem = receivableItems.find((item) => !item.currentStockId);
    if (missingMappedItem) {
      toast.error('Map every product to inventory before receiving the full order.');
      return;
    }

    if (receivableItems.length === 1) {
      openReceiveStockForItem(order, receivableItems[0]);
      return;
    }

    setReceiveRequest({
      mode: 'order',
      order,
      item: {
        id: 'full-order',
        productNameSnapshot: `${receivableItems.length} mapped order items`,
        vendorNameSnapshot: order?.vendorDisplayName,
        quantity: receivableItems.reduce((sum, item) => sum + getRemainingReceiveQuantity(item), 0).toFixed(2),
        receivedQuantity: '0',
        currentStockId: 'multiple',
      },
      receivedQuantity: receivableItems.reduce((sum, item) => sum + getRemainingReceiveQuantity(item), 0).toFixed(2),
      notes: '',
      items: receivableItems,
    });
  };

  const updateReceiveRequest = (patch) => {
    setReceiveRequest((current) => (current ? { ...current, ...patch } : current));
  };

  const handleSubmitReceiveStock = async (event) => {
    event.preventDefault();
    if (!receiveRequest || submittingReceive) return;

    const receiveQuantity = Number(receiveRequest.receivedQuantity);
    const remainingQuantity = getRemainingReceiveQuantity(receiveRequest.item);
    if (receiveRequest.mode === 'item' && (!Number.isFinite(receiveQuantity) || receiveQuantity <= 0)) {
      toast.error('Receive quantity must be greater than 0.');
      return;
    }

    if (receiveRequest.mode === 'item' && receiveQuantity > remainingQuantity) {
      toast.error('Receive quantity cannot exceed the remaining ordered quantity.');
      return;
    }

    if (String(receiveRequest.notes || '').length > RECEIVE_NOTES_MAX_LENGTH) {
      toast.error(`Receipt notes must be ${RECEIVE_NOTES_MAX_LENGTH} characters or fewer.`);
      return;
    }

    setSubmittingReceive(true);
    try {
      const payload = { notes: receiveRequest.notes || '' };
      const result = receiveRequest.mode === 'order'
        ? await receiveSupplyHubOrder(receiveRequest.order.id, payload)
        : await receiveSupplyHubOrderItem(receiveRequest.order.id, receiveRequest.item.id, {
          ...payload,
          receivedQuantity: receiveQuantity,
        });

      toast.success(result?.message || 'Supply Hub stock receipt recorded safely.');
      setReceiveRequest(null);
      await loadOrders();
      if (result?.order) {
        setSelectedOrder(result.order);
      } else if (selectedOrder?.id) {
        await openOrderDetail(selectedOrder.id);
      }
    } catch (error) {
      console.error('Supply Hub receive stock failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setSubmittingReceive(false);
    }
  };

  const handleGenerateRecommendations = async () => {
    if (!canManageRecommendations || generatingRecommendations) return;
    setGeneratingRecommendations(true);
    setRecommendationError('');
    try {
      const result = await generateCurrentSupplyHubRecommendations();
      const generatedCount = Number(result?.generatedCount || 0);
      const skippedCount = Array.isArray(result?.skipped) ? result.skipped.length : 0;
      if (generatedCount > 0) {
        toast.success(`Dektal generated ${generatedCount} Supply Hub suggestion${generatedCount === 1 ? '' : 's'}.`);
      } else if (skippedCount > 0) {
        toast('No new suggestions were created because active suggestions already exist or no approved match was found.');
      } else {
        toast('No low-stock Supply Hub suggestions were needed right now.');
      }
      await loadRecommendations();
    } catch (error) {
      console.error('Supply Hub recommendation generation failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setGeneratingRecommendations(false);
    }
  };

  const handleDismissRecommendation = async (recommendation) => {
    if (!recommendation?.id || !canManageRecommendations) return;
    const dismissReason = window.prompt('Optional reason for dismissing this Supply Hub suggestion:') || '';
    setRecommendationActionId(`dismiss-${recommendation.id}`);
    try {
      await dismissSupplyHubRecommendation(recommendation.id, { dismissReason });
      toast.success('Supply Hub suggestion dismissed.');
      await loadRecommendations();
    } catch (error) {
      console.error('Supply Hub recommendation dismiss failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setRecommendationActionId('');
    }
  };

  const handleCreateMappingFromRecommendation = async (recommendation) => {
    if (!recommendation?.id || !canManageRecommendations) return;
    const currentStockId = recommendation?.currentStock?.currentStockId;
    const supplyHubProductId = recommendation?.supplyHubProduct?.id;
    if (!currentStockId || !supplyHubProductId) {
      toast.error('This suggestion does not have enough safe mapping information.');
      return;
    }

    setRecommendationActionId(`map-${recommendation.id}`);
    try {
      await createSupplyHubProductMapping({
        currentStockId,
        supplyHubProductId,
        confidenceScore: 100,
        matchReason: `Mapped from Supply Hub recommendation ${recommendation.id}`,
      });
      toast.success('Product mapped to this clinic stock item.');
      await Promise.all([loadRecommendations(), loadMappings()]);
      setActiveTab(SUPPLY_HUB_TABS.MAPPINGS);
    } catch (error) {
      console.error('Supply Hub product mapping failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setRecommendationActionId('');
    }
  };

  const handleArchiveMapping = async (mapping) => {
    if (!mapping?.id || !canManageRecommendations) return;
    const confirmed = window.confirm('Archive this Supply Hub product mapping? This will not delete inventory or marketplace product data.');
    if (!confirmed) return;

    setMappingActionId(`archive-mapping-${mapping.id}`);
    try {
      await archiveSupplyHubProductMapping(mapping.id, {
        archiveReason: 'Archived from Supply Hub mapping UI',
      });
      toast.success('Supply Hub mapping archived.');
      await loadMappings();
    } catch (error) {
      console.error('Supply Hub product mapping archive failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setMappingActionId('');
    }
  };

  const openCreateVendorModal = () => {
    setVendorAdminModal({
      mode: 'create',
      vendor: null,
      form: { ...EMPTY_VENDOR_FORM },
    });
  };

  const openEditVendorModal = (vendor) => {
    setVendorAdminModal({
      mode: 'edit',
      vendor,
      form: {
        displayName: vendor?.displayName || '',
        legalName: vendor?.legalName || '',
        contactEmail: vendor?.contactEmail || '',
        contactPhone: vendor?.contactPhone || '',
        serviceAreas: listToCsv(vendor?.serviceAreas),
        websiteUrl: vendor?.websiteUrl || '',
        gstNumber: vendor?.gstNumber || '',
        publicDescription: vendor?.publicDescription || '',
        logoUrl: vendor?.logoUrl || '',
        sponsoredStatus: vendor?.sponsoredStatus || 'none',
      },
    });
  };

  const updateVendorAdminForm = (patch) => {
    setVendorAdminModal((current) => (
      current ? { ...current, form: { ...current.form, ...patch } } : current
    ));
  };

  const openCreateProductModal = () => {
    setProductAdminModal({
      mode: 'create',
      product: null,
      form: { ...EMPTY_PRODUCT_FORM },
    });
  };

  const openEditProductModal = (product) => {
    setProductAdminModal({
      mode: 'edit',
      product,
      form: {
        vendorId: product?.vendorId || '',
        name: product?.name || '',
        category: product?.category || '',
        brand: product?.brand || '',
        unit: product?.unit || '',
        packSize: product?.packSize || '',
        description: product?.description || '',
        imageUrl: product?.imageUrl || '',
        listPrice: product?.listPrice ?? '',
        offerPrice: product?.offerPrice ?? '',
        currency: product?.currency || 'INR',
        availabilityStatus: product?.availabilityStatus || 'available',
        sponsoredStatus: product?.sponsoredStatus || 'none',
        sponsoredRank: product?.sponsoredRank || '',
        tags: listToCsv(product?.tags),
      },
    });
  };

  const updateProductAdminForm = (patch) => {
    setProductAdminModal((current) => (
      current ? { ...current, form: { ...current.form, ...patch } } : current
    ));
  };

  const buildVendorAdminPayload = (form) => ({
    displayName: form.displayName,
    legalName: form.legalName,
    contactEmail: form.contactEmail,
    contactPhone: form.contactPhone,
    serviceAreas: csvToList(form.serviceAreas),
    websiteUrl: form.websiteUrl,
    gstNumber: form.gstNumber,
    publicDescription: form.publicDescription,
    logoUrl: form.logoUrl,
    sponsoredStatus: form.sponsoredStatus || 'none',
  });

  const buildProductAdminPayload = (form) => ({
    vendorId: form.vendorId,
    name: form.name,
    category: form.category,
    brand: form.brand,
    unit: form.unit,
    packSize: form.packSize,
    description: form.description,
    imageUrl: form.imageUrl,
    listPrice: form.listPrice,
    offerPrice: form.offerPrice,
    currency: form.currency || 'INR',
    availabilityStatus: form.availabilityStatus || 'available',
    sponsoredStatus: form.sponsoredStatus || 'none',
    sponsoredRank: form.sponsoredRank,
    tags: csvToList(form.tags),
  });

  const handleSubmitVendorAdmin = async (event) => {
    event.preventDefault();
    if (!isPlatformAdmin || !vendorAdminModal || submittingAdmin) return;
    setSubmittingAdmin(true);
    try {
      const payload = buildVendorAdminPayload(vendorAdminModal.form);
      if (vendorAdminModal.mode === 'edit') {
        await updateAdminSupplyHubVendor(vendorAdminModal.vendor.id, payload);
        toast.success('Supply Hub vendor updated.');
      } else {
        await createAdminSupplyHubVendor(payload);
        toast.success('Supply Hub vendor created for review.');
      }
      setVendorAdminModal(null);
      await Promise.all([loadAdminVendors(), loadVendors()]);
    } catch (error) {
      console.error('Supply Hub admin vendor save failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setSubmittingAdmin(false);
    }
  };

  const handleSubmitProductAdmin = async (event) => {
    event.preventDefault();
    if (!isPlatformAdmin || !productAdminModal || submittingAdmin) return;
    setSubmittingAdmin(true);
    try {
      const payload = buildProductAdminPayload(productAdminModal.form);
      if (productAdminModal.mode === 'edit') {
        await updateAdminSupplyHubProduct(productAdminModal.product.id, payload);
        toast.success('Supply Hub product updated.');
      } else {
        await createAdminSupplyHubProduct(payload);
        toast.success('Supply Hub product created for review.');
      }
      setProductAdminModal(null);
      await Promise.all([loadAdminProducts(), loadProducts()]);
    } catch (error) {
      console.error('Supply Hub admin product save failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setSubmittingAdmin(false);
    }
  };

  const handleApproveVendor = async (vendor) => {
    if (!isPlatformAdmin || !vendor?.id) return;
    setAdminActionId(`approve-vendor-${vendor.id}`);
    try {
      await approveAdminSupplyHubVendor(vendor.id);
      toast.success('Supply Hub vendor approved.');
      await Promise.all([loadAdminVendors(), loadVendors()]);
    } catch (error) {
      console.error('Supply Hub vendor approve failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setAdminActionId('');
    }
  };

  const handleApproveProduct = async (product) => {
    if (!isPlatformAdmin || !product?.id) return;
    setAdminActionId(`approve-product-${product.id}`);
    try {
      await approveAdminSupplyHubProduct(product.id);
      toast.success('Supply Hub product approved.');
      await Promise.all([loadAdminProducts(), loadProducts()]);
    } catch (error) {
      console.error('Supply Hub product approve failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setAdminActionId('');
    }
  };

  const openRejectVendor = (vendor) => {
    setAdminReasonRequest({
      type: 'rejectVendor',
      entity: vendor,
      title: 'Reject Vendor',
      label: 'Rejection Reason',
      reason: '',
    });
  };

  const openSuspendVendor = (vendor) => {
    setAdminReasonRequest({
      type: 'suspendVendor',
      entity: vendor,
      title: 'Suspend Vendor',
      label: 'Suspension Reason',
      reason: '',
    });
  };

  const openRejectProduct = (product) => {
    setAdminReasonRequest({
      type: 'rejectProduct',
      entity: product,
      title: 'Reject Product',
      label: 'Rejection Reason',
      reason: '',
    });
  };

  const updateAdminReason = (reason) => {
    setAdminReasonRequest((current) => (current ? { ...current, reason } : current));
  };

  const handleSubmitAdminReason = async (event) => {
    event.preventDefault();
    if (!isPlatformAdmin || !adminReasonRequest || submittingAdmin) return;
    const reason = String(adminReasonRequest.reason || '').trim();
    if (!reason) {
      toast.error('Reason is required.');
      return;
    }

    const entity = adminReasonRequest.entity;
    setSubmittingAdmin(true);
    setAdminActionId(`${adminReasonRequest.type}-${entity?.id}`);
    try {
      if (adminReasonRequest.type === 'rejectVendor') {
        await rejectAdminSupplyHubVendor(entity.id, { rejectionReason: reason });
        toast.success('Supply Hub vendor rejected.');
      } else if (adminReasonRequest.type === 'suspendVendor') {
        await suspendAdminSupplyHubVendor(entity.id, { suspensionReason: reason });
        toast.success('Supply Hub vendor suspended.');
      } else if (adminReasonRequest.type === 'rejectProduct') {
        await rejectAdminSupplyHubProduct(entity.id, { rejectionReason: reason });
        toast.success('Supply Hub product rejected.');
      }
      setAdminReasonRequest(null);
      await Promise.all([loadAdminVendors(), loadAdminProducts(), loadVendors(), loadProducts()]);
    } catch (error) {
      console.error('Supply Hub admin reason action failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setSubmittingAdmin(false);
      setAdminActionId('');
    }
  };

  const openSponsorProduct = (product) => {
    setSponsorRequest({
      product,
      sponsoredStatus: product?.sponsoredStatus && product.sponsoredStatus !== 'none'
        ? product.sponsoredStatus
        : 'sponsored',
      sponsoredRank: product?.sponsoredRank || '',
    });
  };

  const updateSponsorRequest = (patch) => {
    setSponsorRequest((current) => (current ? { ...current, ...patch } : current));
  };

  const handleSubmitSponsorProduct = async (event) => {
    event.preventDefault();
    if (!isPlatformAdmin || !sponsorRequest?.product?.id || submittingAdmin) return;
    setSubmittingAdmin(true);
    setAdminActionId(`sponsor-product-${sponsorRequest.product.id}`);
    try {
      await sponsorAdminSupplyHubProduct(sponsorRequest.product.id, {
        sponsoredStatus: sponsorRequest.sponsoredStatus,
        sponsoredRank: sponsorRequest.sponsoredRank || null,
      });
      toast.success('Supply Hub product sponsorship updated.');
      setSponsorRequest(null);
      await Promise.all([loadAdminProducts(), loadProducts()]);
    } catch (error) {
      console.error('Supply Hub product sponsor failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setSubmittingAdmin(false);
      setAdminActionId('');
    }
  };

  const handleUnsponsorProduct = async (product) => {
    if (!isPlatformAdmin || !product?.id) return;
    setAdminActionId(`unsponsor-product-${product.id}`);
    try {
      await unsponsorAdminSupplyHubProduct(product.id);
      toast.success('Supply Hub product unsponsored.');
      await Promise.all([loadAdminProducts(), loadProducts()]);
    } catch (error) {
      console.error('Supply Hub product unsponsor failed:', error);
      toast.error(getFriendlySupplyHubError(error));
    } finally {
      setAdminActionId('');
    }
  };

  if (!canBrowseMarketplace) {
    return (
      <div className="min-h-screen bg-white px-4 py-8 md:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <StatusMessage
            type="error"
            title="Supply Hub is restricted for this role"
            message="Owner/Admin, Doctor, Staff, Accountant, and Platform Admin roles can browse approved Supply Hub marketplace listings when the backend permits access."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 pb-10 pt-5 font-sans md:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px] space-y-5">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-600">Supply Hub</p>
            <h1 className="mt-1 text-[28px] font-semibold tracking-tight text-slate-900 md:text-[2.2rem]">
              Dektal Supply Hub
            </h1>
            <p className="mt-1 text-sm text-slate-500">Discover approved dental products and vendors.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-sm font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              Approved marketplace only
            </span>
            {isPlatformAdmin ? (
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3.5 py-2 text-sm font-semibold text-blue-700">
                <ShieldAlert className="h-4 w-4" />
                Admin tools later
              </span>
            ) : null}
            <button
              type="button"
              onClick={
                activeTab === SUPPLY_HUB_TABS.MARKETPLACE
                  ? refreshMarketplace
                  : activeTab === SUPPLY_HUB_TABS.ORDERS
                    ? refreshOrders
                    : activeTab === SUPPLY_HUB_TABS.ADMIN
                      ? refreshAdminMarketplace
                      : refreshRecommendationsAndMappings
              }
              disabled={loadingProducts || loadingVendors || loadingRecommendations || loadingMappings || loadingOrders || loadingAdminVendors || loadingAdminProducts}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 shadow-[0_6px_16px_rgba(15,23,42,0.04)] transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loadingProducts || loadingVendors || loadingRecommendations || loadingMappings || loadingOrders || loadingAdminVendors || loadingAdminProducts ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </header>

        <nav className="flex flex-wrap gap-2">
          <SupplyHubTabButton
            active={activeTab === SUPPLY_HUB_TABS.MARKETPLACE}
            icon={Box}
            label="Marketplace"
            count={productCount}
            onClick={() => setActiveTab(SUPPLY_HUB_TABS.MARKETPLACE)}
          />
          <SupplyHubTabButton
            active={activeTab === SUPPLY_HUB_TABS.RESTOCK}
            icon={ClipboardList}
            label="Restock Suggestions"
            count={recommendationCount}
            onClick={() => setActiveTab(SUPPLY_HUB_TABS.RESTOCK)}
          />
          {canManageRecommendations ? (
            <SupplyHubTabButton
              active={activeTab === SUPPLY_HUB_TABS.MAPPINGS}
              icon={Link2}
              label="Product Mappings"
              count={mappingCount}
              onClick={() => setActiveTab(SUPPLY_HUB_TABS.MAPPINGS)}
            />
          ) : null}
          {canViewOrders ? (
            <SupplyHubTabButton
              active={activeTab === SUPPLY_HUB_TABS.ORDERS}
              icon={ShoppingCart}
              label="My Orders"
              count={orderCount}
              onClick={() => setActiveTab(SUPPLY_HUB_TABS.ORDERS)}
            />
          ) : null}
          {isPlatformAdmin ? (
            <SupplyHubTabButton
              active={activeTab === SUPPLY_HUB_TABS.ADMIN}
              icon={ShieldAlert}
              label="Admin"
              count={adminVendorCount + adminProductCount}
              onClick={() => setActiveTab(SUPPLY_HUB_TABS.ADMIN)}
            />
          ) : null}
        </nav>

        <section className="rounded-[22px] border border-cyan-100 bg-cyan-50 px-5 py-4">
          <p className="text-sm font-semibold text-cyan-900">Dektal Supply Hub suggestions are privacy-safe.</p>
          <p className="mt-1 text-sm leading-6 text-cyan-800">
            Restock suggestions and orders use only this clinic's tenant-scoped data with approved marketplace products. Stock changes happen only through mapped receive-stock actions that create stock ledger movements.
          </p>
        </section>

        {activeTab === SUPPLY_HUB_TABS.MARKETPLACE ? (
          <>
        <section className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <label className="flex-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Search Products</span>
              <div className="relative mt-2">
                <Search className="pointer-events-none absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  type="search"
                  value={productFilters.search}
                  onChange={(event) => updateProductFilter('search', event.target.value)}
                  placeholder="Search by product, brand, category, or description"
                  className="w-full rounded-[16px] border border-slate-200 bg-slate-50 py-3 pl-10 pr-4 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </label>

            <label className="min-w-[160px]">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Category</span>
              <input
                type="text"
                value={productFilters.category}
                onChange={(event) => updateProductFilter('category', event.target.value)}
                className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                placeholder="Any"
              />
            </label>

            <label className="min-w-[160px]">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Brand</span>
              <input
                type="text"
                value={productFilters.brand}
                onChange={(event) => updateProductFilter('brand', event.target.value)}
                className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                placeholder="Any"
              />
            </label>

            <label className="min-w-[180px]">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Vendor</span>
              <select
                value={productFilters.vendorId}
                onChange={(event) => updateProductFilter('vendorId', event.target.value)}
                className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
              >
                <option value="">All vendors</option>
                {vendorOptions.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="min-w-[170px]">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Availability</span>
              <select
                value={productFilters.availabilityStatus}
                onChange={(event) => updateProductFilter('availabilityStatus', event.target.value)}
                className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
              >
                <option value="">Any</option>
                <option value="available">Available</option>
                <option value="out_of_stock">Out of stock</option>
                <option value="discontinued">Discontinued</option>
              </select>
            </label>

            <label className="flex min-h-[46px] items-center gap-2 rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={productFilters.sponsoredOnly}
                onChange={(event) => updateProductFilter('sponsoredOnly', event.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-200"
              />
              Sponsored only
            </label>

            <button
              type="button"
              onClick={() => setProductFilters(EMPTY_PRODUCT_FILTERS)}
              className="rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700"
            >
              Clear
            </button>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
          <section className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-slate-900">Approved Products</h2>
                <p className="text-sm text-slate-500">{productCount} marketplace listings found</p>
              </div>
              <Filter className="h-5 w-5 text-slate-400" />
            </div>

            {loadingProducts ? (
              <div className="flex min-h-[260px] items-center justify-center rounded-[22px] border border-slate-200 bg-white">
                <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  Loading approved products...
                </div>
              </div>
            ) : productError ? (
              <StatusMessage
                type="error"
                title="Unable to load products"
                message={productError}
                actionLabel="Retry Products"
                onAction={loadProducts}
              />
            ) : products.length ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    canCreateOrder={canCreateOrders}
                    onOpenProduct={openProductDetail}
                    onOpenVendor={openVendorDetail}
                    onRequestOrder={openOrderRequestFromProduct}
                  />
                ))}
              </div>
            ) : (
              <StatusMessage
                title="No products found"
                message="No approved Supply Hub products match the current filters yet. Platform-approved products will appear here after they are added."
                actionLabel="Clear Filters"
                onAction={() => setProductFilters(EMPTY_PRODUCT_FILTERS)}
              />
            )}
          </section>

          <aside className="space-y-4">
            <section className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900">Approved Vendors</h2>
              <p className="mt-1 text-sm text-slate-500">{vendorCount} vendors found</p>

              <div className="mt-4 space-y-3">
                <label>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Vendor Search</span>
                  <input
                    type="search"
                    value={vendorFilters.search}
                    onChange={(event) => updateVendorFilter('search', event.target.value)}
                    className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                    placeholder="Search vendors"
                  />
                </label>

                <label>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Service Area</span>
                  <input
                    type="text"
                    value={vendorFilters.serviceArea}
                    onChange={(event) => updateVendorFilter('serviceArea', event.target.value)}
                    className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                    placeholder="City or area"
                  />
                </label>

                <label className="flex items-center gap-2 rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={vendorFilters.sponsoredOnly}
                    onChange={(event) => updateVendorFilter('sponsoredOnly', event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-200"
                  />
                  Sponsored vendors only
                </label>

                <button
                  type="button"
                  onClick={() => setVendorFilters(EMPTY_VENDOR_FILTERS)}
                  className="w-full rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700"
                >
                  Clear Vendor Filters
                </button>
              </div>
            </section>

            {loadingVendors ? (
              <div className="flex min-h-[180px] items-center justify-center rounded-[22px] border border-slate-200 bg-white">
                <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  Loading vendors...
                </div>
              </div>
            ) : vendorError ? (
              <StatusMessage
                type="error"
                title="Unable to load vendors"
                message={vendorError}
                actionLabel="Retry Vendors"
                onAction={loadVendors}
              />
            ) : vendors.length ? (
              <div className="space-y-3">
                {vendors.map((vendor) => (
                  <VendorCard key={vendor.id} vendor={vendor} onOpenVendor={openVendorDetail} />
                ))}
              </div>
            ) : (
              <StatusMessage
                title="No vendors found"
                message="No approved Supply Hub vendors match the current filters yet."
                actionLabel="Clear Vendor Filters"
                onAction={() => setVendorFilters(EMPTY_VENDOR_FILTERS)}
              />
            )}
          </aside>
        </div>
          </>
        ) : null}

        {activeTab === SUPPLY_HUB_TABS.RESTOCK ? (
          <section className="space-y-4">
            <div className="flex flex-col gap-3 rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)] md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-slate-900">Restock Suggestions</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Dektal matches this clinic's low-stock inventory items to approved Supply Hub products.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {canManageRecommendations ? (
                  <button
                    type="button"
                    onClick={handleGenerateRecommendations}
                    disabled={generatingRecommendations}
                    className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <RefreshCw className={`h-4 w-4 ${generatingRecommendations ? 'animate-spin' : ''}`} />
                    {generatingRecommendations ? 'Asking Dektal...' : 'Ask Dektal for Restock Suggestions'}
                  </button>
                ) : (
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-500">
                    Read-only suggestions for this role
                  </span>
                )}
                <button
                  type="button"
                  onClick={loadRecommendations}
                  disabled={loadingRecommendations}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingRecommendations ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>

            <section className="rounded-[22px] border border-amber-200 bg-amber-50 px-5 py-4">
              <p className="text-sm font-semibold text-amber-900">Order requests stay separate from stock receiving.</p>
              <p className="mt-1 text-sm leading-6 text-amber-800">
                Suggestions can open a safe order request. Inventory updates happen later from My Orders only after the product is mapped to this clinic's current stock.
              </p>
            </section>

            {!canViewRecommendations ? (
              <StatusMessage
                type="error"
                title="Restock suggestions are restricted for this role"
                message="Owner/Admin, Doctor, Staff, and Accountant roles can view safe Supply Hub restock suggestions when the backend permits access."
              />
            ) : loadingRecommendations ? (
              <div className="flex min-h-[260px] items-center justify-center rounded-[22px] border border-slate-200 bg-white">
                <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  Loading Supply Hub restock suggestions...
                </div>
              </div>
            ) : recommendationError ? (
              <StatusMessage
                type="error"
                title="Unable to load restock suggestions"
                message={recommendationError}
                actionLabel="Retry Suggestions"
                onAction={loadRecommendations}
              />
            ) : recommendations.length ? (
              <div className="space-y-4">
                {recommendations.map((recommendation) => (
                  <RecommendationCard
                    key={recommendation.id}
                    recommendation={recommendation}
                    canManage={canManageRecommendations}
                    canCreateOrder={canCreateOrders}
                    actioningId={recommendationActionId}
                    onOpenProduct={openProductDetail}
                    onDismiss={handleDismissRecommendation}
                    onCreateMapping={handleCreateMappingFromRecommendation}
                    onRequestOrder={openOrderRequestFromRecommendation}
                  />
                ))}
              </div>
            ) : (
              <StatusMessage
                title="No active restock suggestions"
                message="Generate current suggestions to let Dektal match low-stock consumables to approved Supply Hub products. If no approved match exists, no suggestion will be created."
                actionLabel={canManageRecommendations ? 'Generate Suggestions' : undefined}
                onAction={canManageRecommendations ? handleGenerateRecommendations : undefined}
              />
            )}
          </section>
        ) : null}

        {activeTab === SUPPLY_HUB_TABS.MAPPINGS ? (
          <section className="space-y-4">
            <div className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900">Product Mappings</h2>
              <p className="mt-1 text-sm leading-6 text-slate-500">
                Mappings connect this clinic's stock items to approved marketplace products. Full stock-item search remains a later UI step; this phase supports recommendation-linked mapping safely.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={refreshRecommendationsAndMappings}
                  disabled={loadingMappings || loadingRecommendations}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingMappings || loadingRecommendations ? 'animate-spin' : ''}`} />
                  Refresh Mappings
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab(SUPPLY_HUB_TABS.RESTOCK)}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
                >
                  <ClipboardList className="h-4 w-4" />
                  Map From Suggestions
                </button>
              </div>
            </div>

            {!canManageRecommendations ? (
              <StatusMessage
                type="error"
                title="Product mappings are restricted for this role"
                message="Owner/Admin and Doctor roles can create, update, or archive Supply Hub product mappings when the backend permits access."
              />
            ) : loadingMappings ? (
              <div className="flex min-h-[220px] items-center justify-center rounded-[22px] border border-slate-200 bg-white">
                <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  Loading Supply Hub mappings...
                </div>
              </div>
            ) : mappingError ? (
              <StatusMessage
                type="error"
                title="Unable to load product mappings"
                message={mappingError}
                actionLabel="Retry Mappings"
                onAction={loadMappings}
              />
            ) : mappings.length ? (
              <div className="space-y-4">
                {mappings.map((mapping) => (
                  <MappingCard
                    key={mapping.id}
                    mapping={mapping}
                    canManage={canManageRecommendations}
                    actioningId={mappingActionId}
                    onOpenProduct={openProductDetail}
                    onArchive={handleArchiveMapping}
                  />
                ))}
              </div>
            ) : (
              <StatusMessage
                title="No product mappings yet"
                message="Use an active restock suggestion to map an approved Supply Hub product to this clinic's stock item. This does not modify stock quantities or marketplace product data."
                actionLabel="Open Restock Suggestions"
                onAction={() => setActiveTab(SUPPLY_HUB_TABS.RESTOCK)}
              />
            )}
          </section>
        ) : null}

        {activeTab === SUPPLY_HUB_TABS.ADMIN ? (
          <section className="space-y-5">
            {!isPlatformAdmin ? (
              <StatusMessage
                type="error"
                title="Supply Hub admin is restricted"
                message="Only platform_admin can manage vendor and product approvals."
              />
            ) : (
              <>
                <section className="rounded-[22px] border border-blue-100 bg-blue-50 px-5 py-4">
                  <p className="text-sm font-semibold text-blue-900">Platform marketplace management only.</p>
                  <p className="mt-1 text-sm leading-6 text-blue-800">
                    This admin area manages shared Supply Hub vendor and product records. It does not show clinic inventory, stock levels, order history, purchase records, recommendations, or patient data.
                  </p>
                </section>

                <section className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold tracking-tight text-slate-900">Vendor Approval</h2>
                      <p className="mt-1 text-sm leading-6 text-slate-500">Create, edit, approve, reject, or suspend marketplace vendors.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={loadAdminVendors}
                        disabled={loadingAdminVendors}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <RefreshCw className={`h-4 w-4 ${loadingAdminVendors ? 'animate-spin' : ''}`} />
                        Refresh Vendors
                      </button>
                      <button
                        type="button"
                        onClick={openCreateVendorModal}
                        className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Create Vendor
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
                    <label className="md:col-span-2">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Search Vendors</span>
                      <input
                        value={adminVendorFilters.search}
                        onChange={(event) => updateAdminVendorFilter('search', event.target.value)}
                        className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                        placeholder="Display name, legal name, description"
                      />
                    </label>
                    <label>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Status</span>
                      <select
                        value={adminVendorFilters.verificationStatus}
                        onChange={(event) => updateAdminVendorFilter('verificationStatus', event.target.value)}
                        className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                      >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </label>
                    <label className="flex items-end gap-2 rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={adminVendorFilters.sponsoredOnly}
                        onChange={(event) => updateAdminVendorFilter('sponsoredOnly', event.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600"
                      />
                      <span className="text-sm font-semibold text-slate-600">Sponsored/featured only</span>
                    </label>
                  </div>
                </section>

                {loadingAdminVendors ? (
                  <div className="flex min-h-[180px] items-center justify-center rounded-[22px] border border-slate-200 bg-white">
                    <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      Loading admin vendors...
                    </div>
                  </div>
                ) : adminVendorError ? (
                  <StatusMessage type="error" title="Unable to load admin vendors" message={adminVendorError} actionLabel="Retry Vendors" onAction={loadAdminVendors} />
                ) : adminVendors.length ? (
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {adminVendors.map((vendor) => (
                      <AdminVendorCard
                        key={vendor.id}
                        vendor={vendor}
                        actioningId={adminActionId}
                        onEdit={openEditVendorModal}
                        onApprove={handleApproveVendor}
                        onReject={openRejectVendor}
                        onSuspend={openSuspendVendor}
                      />
                    ))}
                  </div>
                ) : (
                  <StatusMessage title="No admin vendors found" message="Create a vendor foundation record or adjust the filters." actionLabel="Create Vendor" onAction={openCreateVendorModal} />
                )}

                <section className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold tracking-tight text-slate-900">Product Approval</h2>
                      <p className="mt-1 text-sm leading-6 text-slate-500">Create, edit, approve, reject, sponsor, or unsponsor marketplace products.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={loadAdminProducts}
                        disabled={loadingAdminProducts}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <RefreshCw className={`h-4 w-4 ${loadingAdminProducts ? 'animate-spin' : ''}`} />
                        Refresh Products
                      </button>
                      <button
                        type="button"
                        onClick={openCreateProductModal}
                        className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Create Product
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
                    <label className="md:col-span-2">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Search Products</span>
                      <input
                        value={adminProductFilters.search}
                        onChange={(event) => updateAdminProductFilter('search', event.target.value)}
                        className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                        placeholder="Product, brand, category, description"
                      />
                    </label>
                    <label>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Approval</span>
                      <select
                        value={adminProductFilters.approvalStatus}
                        onChange={(event) => updateAdminProductFilter('approvalStatus', event.target.value)}
                        className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                      >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </label>
                    <label>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Availability</span>
                      <select
                        value={adminProductFilters.availabilityStatus}
                        onChange={(event) => updateAdminProductFilter('availabilityStatus', event.target.value)}
                        className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                      >
                        <option value="">All</option>
                        <option value="available">Available</option>
                        <option value="out_of_stock">Out of stock</option>
                        <option value="discontinued">Discontinued</option>
                        <option value="hidden">Hidden</option>
                      </select>
                    </label>
                    <label>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Vendor</span>
                      <select
                        value={adminProductFilters.vendorId}
                        onChange={(event) => updateAdminProductFilter('vendorId', event.target.value)}
                        className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                      >
                        <option value="">All vendors</option>
                        {adminVendors.map((vendor) => (
                          <option key={vendor.id} value={vendor.id}>{vendor.displayName}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Category</span>
                      <input value={adminProductFilters.category} onChange={(event) => updateAdminProductFilter('category', event.target.value)} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
                    </label>
                    <label>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Brand</span>
                      <input value={adminProductFilters.brand} onChange={(event) => updateAdminProductFilter('brand', event.target.value)} className="mt-2 w-full rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100" />
                    </label>
                    <label className="flex items-end gap-2 rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={adminProductFilters.sponsoredOnly}
                        onChange={(event) => updateAdminProductFilter('sponsoredOnly', event.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600"
                      />
                      <span className="text-sm font-semibold text-slate-600">Sponsored/featured only</span>
                    </label>
                  </div>
                </section>

                {loadingAdminProducts ? (
                  <div className="flex min-h-[180px] items-center justify-center rounded-[22px] border border-slate-200 bg-white">
                    <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      Loading admin products...
                    </div>
                  </div>
                ) : adminProductError ? (
                  <StatusMessage type="error" title="Unable to load admin products" message={adminProductError} actionLabel="Retry Products" onAction={loadAdminProducts} />
                ) : adminProducts.length ? (
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {adminProducts.map((product) => (
                      <AdminProductCard
                        key={product.id}
                        product={product}
                        actioningId={adminActionId}
                        onEdit={openEditProductModal}
                        onApprove={handleApproveProduct}
                        onReject={openRejectProduct}
                        onSponsor={openSponsorProduct}
                        onUnsponsor={handleUnsponsorProduct}
                      />
                    ))}
                  </div>
                ) : (
                  <StatusMessage title="No admin products found" message="Create a product foundation record or adjust the filters." actionLabel="Create Product" onAction={openCreateProductModal} />
                )}
              </>
            )}
          </section>
        ) : null}

        {activeTab === SUPPLY_HUB_TABS.ORDERS ? (
          <section className="space-y-4">
            <div className="flex flex-col gap-3 rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)] md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-slate-900">My Orders</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Tenant-private Supply Hub order requests for this clinic only.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={loadOrders}
                  disabled={loadingOrders}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingOrders ? 'animate-spin' : ''}`} />
                  Refresh Orders
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab(SUPPLY_HUB_TABS.MARKETPLACE)}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
                >
                  <Box className="h-4 w-4" />
                  Browse Products
                </button>
              </div>
            </div>

            <section className="rounded-[22px] border border-amber-200 bg-amber-50 px-5 py-4">
              <p className="text-sm font-semibold text-amber-900">Receive stock is ledger-protected.</p>
              <p className="mt-1 text-sm leading-6 text-amber-800">
                Receive stock is available only for tenant-owned orders with mapped inventory items. The backend updates current stock and writes an append-only stock ledger movement together.
              </p>
            </section>

            {!canViewOrders ? (
              <StatusMessage
                type="error"
                title="Supply Hub orders are restricted for this role"
                message="Owner/Admin, Doctor, Staff, and Accountant roles can view safe tenant-scoped Supply Hub order records when the backend permits access."
              />
            ) : loadingOrders ? (
              <div className="flex min-h-[240px] items-center justify-center rounded-[22px] border border-slate-200 bg-white">
                <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  Loading Supply Hub orders...
                </div>
              </div>
            ) : orderError ? (
              <StatusMessage
                type="error"
                title="Unable to load Supply Hub orders"
                message={orderError}
                actionLabel="Retry Orders"
                onAction={loadOrders}
              />
            ) : orders.length ? (
              <div className="space-y-4">
                {orders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    canManage={canManageOrders}
                    actioningId={orderActionId}
                    onOpenOrder={openOrderDetail}
                    onCancel={handleCancelOrder}
                    onOpenReceive={openReceiveStockForOrder}
                  />
                ))}
              </div>
            ) : (
              <StatusMessage
                title="No Supply Hub orders yet"
                message="Create an order request from an approved marketplace product or a restock suggestion. Receiving stock happens later from a mapped order item."
                actionLabel="Browse Marketplace"
                onAction={() => setActiveTab(SUPPLY_HUB_TABS.MARKETPLACE)}
              />
            )}
          </section>
        ) : null}
      </div>

      {detailLoading ? (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-900/20">
          <div className="flex items-center gap-3 rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-[0_18px_44px_rgba(15,23,42,0.14)]">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            Loading details...
          </div>
        </div>
      ) : null}

      {selectedProduct ? (
        <ModalShell
          eyebrow="Supply Hub Product"
          title={selectedProduct.name || 'Product Details'}
          onClose={() => setSelectedProduct(null)}
        >
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
            <SafeImage
              src={selectedProduct.imageUrl}
              alt={selectedProduct.name || 'Supply Hub product'}
              className="aspect-square w-full rounded-[20px] border border-slate-200"
            />

            <div className="space-y-5">
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getAvailabilityClasses(selectedProduct.availabilityStatus)}`}>
                  {formatLabel(selectedProduct.availabilityStatus)}
                </span>
                {selectedProduct.sponsoredStatus && selectedProduct.sponsoredStatus !== 'none' ? (
                  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getSponsoredClasses(selectedProduct.sponsoredStatus)}`}>
                    {formatLabel(selectedProduct.sponsoredStatus)}
                  </span>
                ) : null}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Brand</p>
                  <p className="mt-1 font-semibold text-slate-900">{selectedProduct.brand || '-'}</p>
                </div>
                <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Category</p>
                  <p className="mt-1 font-semibold text-slate-900">{selectedProduct.category || '-'}</p>
                </div>
                <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Unit / Pack</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {[selectedProduct.unit, selectedProduct.packSize].filter(Boolean).join(' / ') || '-'}
                  </p>
                </div>
                <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Public Price</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {formatMoney(selectedProduct.offerPrice ?? selectedProduct.listPrice, selectedProduct.currency)}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Description</p>
                <p className="mt-2 text-sm leading-6 text-slate-700">
                  {selectedProduct.description || 'No public product description has been added yet.'}
                </p>
              </div>

              <div className="rounded-[18px] border border-slate-200 bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Approved Vendor</p>
                <button
                  type="button"
                  onClick={() => selectedProduct.vendor?.id && openVendorDetail(selectedProduct.vendor.id)}
                  className="mt-2 text-left text-base font-semibold text-blue-700 transition hover:text-blue-800"
                >
                  {selectedProduct.vendor?.displayName || 'Approved vendor'}
                </button>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {selectedProduct.vendor?.publicDescription || 'No public vendor description has been added yet.'}
                </p>
              </div>

              <div className="rounded-[18px] border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-semibold text-amber-900">Order request is safe for this phase.</p>
                <p className="mt-1 text-sm leading-6 text-amber-800">
                  Request Order creates a tenant-private order request only. It does not receive stock, update inventory quantities, or write stock ledger records.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => openOrderRequestFromProduct(selectedProduct)}
                    disabled={!canCreateOrders}
                    title={canCreateOrders ? 'Create a tenant-private Supply Hub order request.' : 'Your role cannot create Supply Hub order requests.'}
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Request Order
                  </button>
                  <button
                    type="button"
                    disabled
                    title="Open Restock Suggestions to map a recommended product to a clinic stock item."
                    onClick={() => showPlaceholder('Open Restock Suggestions to map a recommended product to a clinic stock item.')}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-500"
                  >
                    Map to Inventory
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalShell>
      ) : null}

      {orderRequest ? (
        <OrderRequestModal
          orderRequest={orderRequest}
          submitting={submittingOrder}
          onClose={() => {
            if (!submittingOrder) {
              setOrderRequest(null);
            }
          }}
          onChange={updateOrderRequest}
          onSubmit={handleSubmitOrderRequest}
        />
      ) : null}

      {receiveRequest ? (
        <ReceiveStockModal
          receiveRequest={receiveRequest}
          submitting={submittingReceive}
          onClose={() => {
            if (!submittingReceive) {
              setReceiveRequest(null);
            }
          }}
          onChange={updateReceiveRequest}
          onSubmit={handleSubmitReceiveStock}
          onOpenMappings={openProductMappingsForReceive}
        />
      ) : null}

      {selectedOrder ? (
        <OrderDetailModal
          order={selectedOrder}
          canManage={canManageOrders}
          actioningId={orderActionId}
          onClose={() => setSelectedOrder(null)}
          onCancel={handleCancelOrder}
          onOpenReceiveItem={openReceiveStockForItem}
          onOpenReceiveOrder={openReceiveStockForOrder}
          onOpenMappings={openProductMappingsForReceive}
        />
      ) : null}

      {vendorAdminModal ? (
        <AdminVendorModal
          mode={vendorAdminModal.mode}
          form={vendorAdminModal.form}
          submitting={submittingAdmin}
          onClose={() => {
            if (!submittingAdmin) {
              setVendorAdminModal(null);
            }
          }}
          onChange={updateVendorAdminForm}
          onSubmit={handleSubmitVendorAdmin}
        />
      ) : null}

      {productAdminModal ? (
        <AdminProductModal
          mode={productAdminModal.mode}
          form={productAdminModal.form}
          vendors={adminVendors}
          submitting={submittingAdmin}
          onClose={() => {
            if (!submittingAdmin) {
              setProductAdminModal(null);
            }
          }}
          onChange={updateProductAdminForm}
          onSubmit={handleSubmitProductAdmin}
        />
      ) : null}

      {adminReasonRequest ? (
        <AdminReasonModal
          title={adminReasonRequest.title}
          label={adminReasonRequest.label}
          reason={adminReasonRequest.reason}
          submitting={submittingAdmin}
          onChange={updateAdminReason}
          onClose={() => {
            if (!submittingAdmin) {
              setAdminReasonRequest(null);
            }
          }}
          onSubmit={handleSubmitAdminReason}
        />
      ) : null}

      {sponsorRequest ? (
        <SponsorProductModal
          product={sponsorRequest.product}
          sponsorRequest={sponsorRequest}
          submitting={submittingAdmin}
          onChange={updateSponsorRequest}
          onClose={() => {
            if (!submittingAdmin) {
              setSponsorRequest(null);
            }
          }}
          onSubmit={handleSubmitSponsorProduct}
        />
      ) : null}

      {selectedVendor ? (
        <ModalShell
          eyebrow="Supply Hub Vendor"
          title={selectedVendor.displayName || 'Vendor Details'}
          onClose={() => setSelectedVendor(null)}
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-[160px_minmax(0,1fr)]">
            <SafeImage
              src={selectedVendor.logoUrl}
              alt={selectedVendor.displayName || 'Supply Hub vendor'}
              className="h-40 w-40 rounded-[20px] border border-slate-200"
            />
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  {formatLabel(selectedVendor.verificationStatus, 'Approved')}
                </span>
                {selectedVendor.sponsoredStatus && selectedVendor.sponsoredStatus !== 'none' ? (
                  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getSponsoredClasses(selectedVendor.sponsoredStatus)}`}>
                    {formatLabel(selectedVendor.sponsoredStatus)}
                  </span>
                ) : null}
              </div>

              <p className="text-sm leading-6 text-slate-700">
                {selectedVendor.publicDescription || 'No public vendor description has been added yet.'}
              </p>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Service Areas</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">
                    {Array.isArray(selectedVendor.serviceAreas) && selectedVendor.serviceAreas.length
                      ? selectedVendor.serviceAreas.join(', ')
                      : 'Not listed'}
                  </p>
                </div>
                <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Website</p>
                  {selectedVendor.websiteUrl ? (
                    <a
                      href={selectedVendor.websiteUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 block break-words text-sm font-semibold text-blue-700 hover:text-blue-800"
                    >
                      {selectedVendor.websiteUrl}
                    </a>
                  ) : (
                    <p className="mt-1 text-sm font-semibold text-slate-900">Not listed</p>
                  )}
                </div>
              </div>

              <div className="rounded-[18px] border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-900">Vendor privacy note</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  This view shows approved public vendor information only. It does not include internal platform notes, clinic purchase history, clinic stock levels, order totals, or another clinic's procurement data.
                </p>
              </div>
            </div>
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
}
